// The table's rules, read from its game system (issue 1303): every rule the table applies — the attack roll and its
// critical, the defences, the miss chances, the minimum damage, initiative and its tie-breaks, measuring, the bonus
// types that stack and the units of time — comes from the system's data on the site (`features.combat`, issue 1219,
// format in docs/MRCS/combat-rules.md; `features.effects.stacking` and `.units`), carried to the table by the site's
// `GET /api/registry` as `rules: { combat, effects: { stacking, units } }`, and handed to the pure functions here.
//
// NOTHING IS DEFAULTED. A part the system does not declare, or declares in a shape this parser does not accept, is
// null, and the table offers no automation for it (no attack, no automatic roll-off, squares without a unit) — never
// another system's numbers. `SystemRules` is the one resolved object a table plays by, so a homebrew layer (#1310) or a
// borrowed system (#1305) is a different object passed in the same way.
import { parseDamageRules, stopsDamage } from './damageStops.js';
import { CONDITION_READS } from './modifiers.js';
import { parseMagicDice, parseStandingPowers } from './magicDice.js';
import { parseCyber } from './cyber.js';
import { ADVANTAGE_LIMITS, advantageDice, isAdvantageWord } from './advantage.js';
import { parseDiceBoost } from '../dice/dice.js';
import { parseMissChance, parseWeaponReach } from './weaponReach.js';
import { parseCasting, parseCover } from './cover.js';
import { parseFlanking, parseSneakAttack, parseStances } from './combatOptions.js';
import { parseMoveFeats } from './moveFeats.js';
import { parseSpecialRules } from './specials.js';
import { parseCharge } from './charge.js';
import { parseReadyRule } from '../state/ready.js';
import { parseManeuverRules, maneuverView, parseManeuverView } from './maneuver.js';
import { parseVisionRules } from '../sight/vision.js';
import { parseWeatherRules, WEATHER_LIMITS } from '../sight/weather.js';
import { copyLevels, parseLevels, woundsOf } from './wounds.js';
/**
 * `spell` and `drain` (issue 1530): the Sorcery test that casts a spell and the drain resistance after it. `astral`
 * (issue 1531): every test of astral combat a creature in an astral state that names the pool makes. `vehicle` (issue
 * 1533): a test of controlling a vehicle (`features.combat.vehicles`). `matrix` (issue
 * 1532): a decker's System Test.
 */
export const POOL_FOR = ['attack', 'dodge', 'resist', 'spell', 'drain', 'astral', 'matrix', 'vehicle', 'any', 'between_turns', 'save_override'];
export const POOL_USES = ['reroll_failures', 'buy_dice', 'buy_success', 'avoid_disaster'];
/** The most pools a system declares (`MAX_POOLS`, scripts/mrcs/combat-rules.js). */
export const MAX_POOLS = 8;
export const ATTACK_KINDS = ['ranged', 'melee'];
/** The validator's bounds (scripts/mrcs/combat-rules.js: MAX_FIRING_MODES, MAX_ROUNDS, MAX_FIRING_STEP). */
export const FIRING_LIMITS = { modes: 8, rounds: 100, step: 10, compensation: 20 };
const FIRING_KEY_RE = /^[A-Z]{1,4}$/;
/** `attack.firing` (issue 1529), strictly; null when malformed, so no mode is offered rather than a wrong one. */
export function parseFiring(v) {
    if (!isObj(v) || Object.keys(v).some((k) => !['modes', 'heavy'].includes(k)) || !Array.isArray(v.modes) || !v.modes.length || v.modes.length > FIRING_LIMITS.modes)
        return null;
    const modes = [];
    const step = (x, lo) => (x === undefined ? null : isInt(x, lo, lo === 0 ? FIRING_LIMITS.step : FIRING_LIMITS.rounds) ? x : undefined);
    for (const m of v.modes) {
        if (!isObj(m) || Object.keys(m).some((k) => !['key', 'label', 'rounds', 'recoil', 'free', 'power', 'level_per', 'dodge_per'].includes(k)))
            return null;
        if (typeof m.key !== 'string' || !FIRING_KEY_RE.test(m.key) || !isLabel(m.label) || modes.some((x) => x.key === m.key))
            return null;
        let rounds = 1;
        if (isObj(m.rounds)) {
            const r = m.rounds;
            if (Object.keys(r).some((k) => k !== 'least' && k !== 'most') || !isInt(r.least, 1, FIRING_LIMITS.rounds) || !isInt(r.most, r.least, FIRING_LIMITS.rounds))
                return null;
            rounds = { least: r.least, most: r.most };
        }
        else if (m.rounds !== undefined) {
            if (!isInt(m.rounds, 1, FIRING_LIMITS.rounds))
                return null;
            rounds = m.rounds;
        }
        const recoil = step(m.recoil, 0);
        const free = step(m.free, 0);
        const power = step(m.power, 0);
        const levelPer = step(m.level_per, 1);
        const dodgePer = step(m.dodge_per, 1);
        if (recoil === undefined || free === undefined || power === undefined || levelPer === undefined || dodgePer === undefined)
            return null;
        modes.push({ key: m.key, label: m.label.trim(), rounds, recoil: recoil ?? 0, free: free ?? 0, power: power ?? 0, levelPer, dodgePer });
    }
    let heavy = null;
    if (v.heavy !== undefined) {
        const h = v.heavy;
        if (!isObj(h) || Object.keys(h).some((k) => k !== 'subtypes' && k !== 'times') || !Array.isArray(h.subtypes) || !h.subtypes.length || h.subtypes.length > 24 || !h.subtypes.every((x) => isLabel(x, 60)) || !isInt(h.times, 2, 5))
            return null;
        heavy = { subtypes: h.subtypes.map((x) => x.trim()), times: h.times };
    }
    return { modes, heavy };
}
/** `attack.firing` back in the data's words. */
export const firingData = (f) => ({
    modes: f.modes.map((m) => ({ key: m.key, label: m.label, ...(m.rounds !== 1 ? { rounds: typeof m.rounds === 'number' ? m.rounds : { ...m.rounds } } : {}), ...(m.recoil ? { recoil: m.recoil } : {}), ...(m.free ? { free: m.free } : {}), ...(m.power ? { power: m.power } : {}), ...(m.levelPer !== null ? { level_per: m.levelPer } : {}), ...(m.dodgePer !== null ? { dodge_per: m.dodgePer } : {}) })),
    ...(f.heavy ? { heavy: { subtypes: [...f.heavy.subtypes], times: f.heavy.times } } : {}),
});
/** The validator's bounds (scripts/mrcs/combat-rules.js: MAX_TARGET, MAX_TARGET_MODIFIER, MAX_RATING_FIELDS). */
export const STAGED_LIMITS = { target: 100, modifier: 20, ratingFields: 4, boxes: 1000, stagePer: 20 };
function creatureTypes(v) {
    const key = (x) => typeof x === 'string' && /^[a-z][a-z0-9_]{0,62}$/.test(x);
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const c = v;
    if (!key(c.field) || !key(c.otherwise) || !key(c.character) || typeof c.kinds !== 'object' || c.kinds === null || Array.isArray(c.kinds))
        return null;
    const kinds = Object.entries(c.kinds);
    if (kinds.length > 16 || !kinds.every(([k, l]) => key(k) && Array.isArray(l) && l.length <= 32 && l.every((t) => typeof t === 'string' && t.length >= 1 && t.length <= 64)))
        return null;
    return { field: c.field, otherwise: c.otherwise, character: c.character, kinds: Object.fromEntries(kinds.map(([k, l]) => [k, l.map((t) => t.toLowerCase())])) };
}
/** A stat block's type, as a kind: null with no type to read. */
export function kindOfType(c, type) {
    const t = (type ?? '').trim().toLowerCase();
    if (!t)
        return null;
    return Object.entries(c.kinds).find(([, l]) => l.includes(t))?.[0] ?? c.otherwise;
}
export const AREA_INCLUDES = ['any_part', 'centre', 'all'];
const stanceView = (s) => ({ key: s.key, label: s.label, attack: s.attack, ac: s.ac });
const stanceViews = (v) => (Array.isArray(v) ? v.filter((x) => isObj(x) && isKey(x.key) && isLabel(x.label) && isInt(x.attack, -50, 50) && isInt(x.ac, -50, 50)).map((x) => ({ key: x.key, label: x.label, attack: x.attack, ac: x.ac })).slice(0, 12) : []);
/** The most resources a system declares, and so a token carries (`MAX_RESOURCES`, scripts/mrcs/combat-rules.js). */
export const MAX_RESOURCES = 12;
export const noRules = () => ({ combat: null, stacking: null, units: null });
const MAX_SOURCES = 200;
/** The registry's `sources`: each well formed, or dropped (a rule named by no layer is the system's). */
export function parseSources(v) {
    const out = {};
    if (!isObj(v))
        return out;
    for (const [path, s] of Object.entries(v).slice(0, MAX_SOURCES)) {
        if (!/^\/(combat|effects)(\/[A-Za-z0-9_.-]{1,64}){0,6}$/.test(path) || !isObj(s))
            continue;
        const ruling = s.ruling === true && s.layer === 0;
        if ((!ruling && !isInt(s.layer, 1, Number.MAX_SAFE_INTEGER)) || !isLabel(s.name, 120) || typeof s.released !== 'boolean')
            continue;
        out[path] = { layer: ruling ? 0 : s.layer, name: s.name.trim(), released: s.released, ...(ruling ? { ruling: true } : {}) };
    }
    return out;
}
/**
 * The layer that set any of these rules (by path, first found), or null when they are the system's own. A path counts
 * when a layer set it or a part holding it (a layer that gave a system with no criticals a whole `critical`).
 */
export function sourceOf(sources, paths) {
    if (!sources)
        return null;
    for (const p of paths) {
        for (let at = p; at.length > 0; at = at.slice(0, at.lastIndexOf('/'))) {
            if (sources[at])
                return sources[at];
        }
    }
    return null;
}
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const isLabel = (v, max = 60) => typeof v === 'string' && v.trim().length > 0 && v.length <= max;
const isKey = (v) => typeof v === 'string' && KEY_RE.test(v);
const oneOf = (v, list) => typeof v === 'string' && list.includes(v);
/** The data format's bounds on a throw (scripts/mrcs/combat-rules.js): up to twenty dice of up to a thousand sides. */
const MAX_DICE = 20;
const MAX_SIDES = 1000;
const MAX_EXPLODE = 10;
const MAX_MARGIN = 1000;
/**
 * A throw from the data (`dice` in `features.combat`), strictly: exact shape or null. The format's own spelling (`at_least`,
 * `{ highest: n }`) is read into the table's; nothing is defaulted, so a success count without `modifiers` is none.
 */
export function parseDice(v) {
    if (!isObj(v) || !isInt(v.count, 1, MAX_DICE) || !isInt(v.sides, 2, MAX_SIDES))
        return null;
    if (Object.keys(v).some((k) => !['count', 'sides', 'explode', 'keep', 'success', 'advantage'].includes(k)))
        return null;
    const out = { count: v.count, sides: v.sides };
    if (v.advantage !== undefined) {
        const a = v.advantage;
        if (v.count !== 1 || v.explode !== undefined || v.keep !== undefined || v.success !== undefined)
            return null;
        if (!isObj(a) || Object.keys(a).length !== 1 || !isInt(a.dice, 1, ADVANTAGE_LIMITS.extraDice))
            return null;
        out.advantage = { dice: a.dice };
    }
    if (v.explode !== undefined) {
        const e = v.explode;
        if (!isObj(e) || Object.keys(e).length !== 2 || !isInt(e.on, 2, v.sides) || !isInt(e.limit, 1, MAX_EXPLODE))
            return null;
        out.explode = { on: e.on, limit: e.limit };
    }
    if (v.keep !== undefined) {
        const k = v.keep;
        const which = isObj(k) ? Object.keys(k) : [];
        if (!isObj(k) || which.length !== 1 || !oneOf(which[0], ['highest', 'lowest']) || !isInt(k[which[0]], 1, v.count - 1))
            return null;
        out.keep = { which: which[0], n: k[which[0]] };
    }
    if (v.success !== undefined) {
        const x = v.success;
        if (!isObj(x) || Object.keys(x).length !== 2 || !isInt(x.at_least, 1, v.sides) || !oneOf(x.modifiers, ['add', 'dice']))
            return null;
        // Every die succeeding and none exploding is a count that cannot vary: no throw at all (the review of 2026-10-10).
        if (x.at_least <= 1 && v.explode === undefined)
            return null;
        out.success = { atLeast: x.at_least, modifiers: x.modifiers };
    }
    return out;
}
const dice = parseDice;
/** The format's spelling of a throw, as the site's data writes it: the inverse of `parseDice`. */
export function diceData(d) {
    return {
        count: d.count, sides: d.sides,
        ...(d.explode ? { explode: { on: d.explode.on, limit: d.explode.limit } } : {}),
        ...(d.keep ? { keep: { [d.keep.which]: d.keep.n } } : {}),
        ...(d.success ? { success: { at_least: d.success.atLeast, modifiers: d.success.modifiers } } : {}),
        ...(d.advantage ? { advantage: { dice: d.advantage.dice } } : {}),
    };
}
/** A throw in the table's own spelling (a stamped record, a snapshot): read back through `parseDice`, exact shape or null. */
export function parseTableDice(v) {
    if (!isObj(v) || v.advantage !== undefined)
        return null;
    const keep = v.keep === undefined ? undefined : isObj(v.keep) && oneOf(v.keep.which, ['highest', 'lowest']) && Object.keys(v.keep).length === 2 ? { [v.keep.which]: v.keep.n } : null;
    const success = v.success === undefined ? undefined : isObj(v.success) && Object.keys(v.success).length === 2 ? { at_least: v.success.atLeast, modifiers: v.success.modifiers } : null;
    return parseDice({ ...v, ...(keep !== undefined ? { keep } : {}), ...(success !== undefined ? { success } : {}) });
}
/**
 * A test's dice from the data (§3.2.1), strictly: exact shape or null. What `parseDice` reads, and the test's own words:
 * a `rated` count, an `at_least` of `target` with its `min_target`, `chains: "sum"`, `explode.above_target` (only against
 * a TN the throw gives) and `disaster: "all_ones"`; the modifier word `target` only with a TN the throw gives. A rated
 * keep is checked at the throw, so here only its bounds.
 */
export function parseTestDice(v) {
    if (!isObj(v) || !(v.count === 'rated' || isInt(v.count, 1, MAX_DICE)) || !isInt(v.sides, 2, MAX_SIDES))
        return null;
    if (Object.keys(v).some((k) => !['count', 'sides', 'explode', 'keep', 'success'].includes(k)))
        return null;
    const sides = v.sides;
    const out = { count: v.count, sides };
    const x = v.success;
    const target = isObj(x) && x.at_least === 'target';
    if (v.explode !== undefined) {
        const e = v.explode;
        if (!isObj(e) || Object.keys(e).some((k) => !['on', 'limit', 'above_target'].includes(k)) || !isInt(e.on, 2, sides) || !isInt(e.limit, 1, MAX_EXPLODE))
            return null;
        if (e.above_target !== undefined && !(e.above_target === true && target))
            return null;
        out.explode = { on: e.on, limit: e.limit, ...(e.above_target ? { aboveTarget: true } : {}) };
    }
    if (v.keep !== undefined) {
        const k = v.keep;
        const which = isObj(k) ? Object.keys(k) : [];
        const most = v.count === 'rated' ? MAX_DICE : v.count - 1;
        if (!isObj(k) || which.length !== 1 || !oneOf(which[0], ['highest', 'lowest']) || !isInt(k[which[0]], 1, most))
            return null;
        out.keep = { which: which[0], n: k[which[0]] };
    }
    if (x !== undefined) {
        if (!isObj(x) || Object.keys(x).some((k) => !['at_least', 'modifiers', 'min_target', 'chains', 'disaster'].includes(k)))
            return null;
        if (!target && !isInt(x.at_least, 1, sides))
            return null;
        if (!target && x.at_least <= 1 && v.explode === undefined)
            return null;
        if (!oneOf(x.modifiers, ['add', 'dice', 'target']) || (x.modifiers === 'target' && !target))
            return null;
        if (x.min_target !== undefined && !(target && isInt(x.min_target, 2, sides)))
            return null;
        if (x.chains !== undefined && x.chains !== 'sum')
            return null;
        if (x.disaster !== undefined && x.disaster !== 'all_ones')
            return null;
        out.success = {
            atLeast: target ? 'target' : x.at_least, modifiers: x.modifiers,
            ...(x.min_target !== undefined ? { minTarget: x.min_target } : {}),
            ...(x.chains ? { chains: 'sum' } : {}), ...(x.disaster ? { disaster: 'all_ones' } : {}),
        };
    }
    return out;
}
const DEFAULT_TO = ['skill', 'specialization', 'attribute'];
const DEFAULT_POOL = ['half_rating', 'half_base', 'none'];
/** `features.combat.test` (§3.2.2), strictly: a test whose dice count successes, an open test that is a total, routes each once. */
function parseTest(v) {
    if (!isObj(v) || Object.keys(v).some((k) => !['dice', 'open', 'defaulting', 'ties'].includes(k)))
        return null;
    const d = parseTestDice(v.dice);
    if (!d || !d.success)
        return null;
    const open = v.open === undefined ? null : parseTestDice(v.open);
    if (v.open !== undefined && (!open || open.success))
        return null;
    let defaulting = null;
    if (v.defaulting !== undefined) {
        const g = v.defaulting;
        if (!isObj(g) || Object.keys(g).some((k) => !['limit', 'routes'].includes(k)) || !Array.isArray(g.routes) || !g.routes.length || g.routes.length > 24)
            return null;
        if (g.limit !== undefined && !isInt(g.limit, 2, MAX_SIDES))
            return null;
        const routes = [];
        for (const r of g.routes) {
            if (!isObj(r) || Object.keys(r).length !== 3 || !oneOf(r.to, DEFAULT_TO) || !isInt(r.target, 0, 20) || !oneOf(r.pool, DEFAULT_POOL))
                return null;
            if (routes.some((x) => x.to === r.to))
                return null;
            routes.push({ to: r.to, target: r.target, pool: r.pool });
        }
        defaulting = { limit: g.limit === undefined ? null : g.limit, routes };
    }
    if (v.ties !== undefined && v.ties !== 'none')
        return null;
    return { dice: d, open, defaulting, ties: v.ties === 'none' ? 'none' : null };
}
const POOL_REFRESH = ['round', 'dm', 'own_turn'];
const POOL_CAP = ['rating', 'half_rating'];
/**
 * `features.combat.pools` (§3.2.3), strictly: at most eight, each key once, every word from its closed list, a cap only
 * for a kind of test that draws on the pool. Anything else is no pools at all, never part of them.
 */
/** `damage.codes` (§3.2.5), strictly: rising levels each with its letters and boxes, the staging step, what the top does. */
export function parseCodes(v) {
    if (!isObj(v) || Object.keys(v).some((k) => !['levels', 'stage_per', 'beyond_top', 'wielder'].includes(k)))
        return null;
    const w = v.wielder;
    if (w !== undefined && !(isObj(w) && Object.keys(w).every((k) => ['purpose', 'which', 'label'].includes(k)) && isKey(w.purpose) && isKey(w.which) && isLabel(w.label, 40)))
        return null;
    if (!Array.isArray(v.levels) || v.levels.length < 1 || v.levels.length > 12)
        return null;
    const levels = [];
    for (const l of v.levels) {
        if (!isObj(l) || Object.keys(l).some((k) => !['key', 'abbr', 'label', 'boxes'].includes(k)) || !isKey(l.key) || !isLabel(l.label, 24))
            return null;
        if (typeof l.abbr !== 'string' || !/^[A-Z]{1,3}$/.test(l.abbr) || !isInt(l.boxes, 1, STAGED_LIMITS.boxes) || (levels.length && l.boxes <= levels[levels.length - 1].boxes))
            return null;
        levels.push({ key: l.key, abbr: l.abbr, label: l.label.trim(), boxes: l.boxes });
    }
    if (new Set(levels.map((l) => l.key)).size !== levels.length || new Set(levels.map((l) => l.abbr)).size !== levels.length)
        return null;
    if (!isInt(v.stage_per, 1, STAGED_LIMITS.stagePer))
        return null;
    const b = v.beyond_top === undefined ? {} : v.beyond_top;
    if (!isObj(b) || Object.entries(b).some(([k, w]) => !oneOf(k, ATTACK_KINDS) || !oneOf(w, ['power', 'none'])))
        return null;
    return { levels, stagePer: v.stage_per, beyondTop: { ranged: b.ranged ?? 'none', melee: b.melee ?? 'none' }, ...(isObj(w) ? { wielder: { purpose: w.purpose, which: w.which, label: w.label.trim() } } : {}) };
}
/** `damage.codes` back in the data's words. */
export const codesData = (c) => ({ levels: c.levels.map((l) => ({ ...l })), stage_per: c.stagePer, beyond_top: { ...c.beyondTop }, ...(c.wielder ? { wielder: { ...c.wielder } } : {}) });
const parseBands = (v) => {
    const out = keyed(v, (e) => (Object.keys(e).every((k) => ['key', 'label', 'target'].includes(k)) && isInt(e.target, 1, STAGED_LIMITS.target) ? { key: e.key, label: e.label.trim(), target: e.target } : null));
    return out && out.length ? out : null;
};
const parseSituations = (v) => keyed(v, (e) => {
    if (!Object.keys(e).every((k) => ['key', 'label', 'target', 'only', 'per', 'most'].includes(k)) || !isInt(e.target, -STAGED_LIMITS.modifier, STAGED_LIMITS.modifier) || e.target === 0)
        return null;
    if ((e.only !== undefined && e.only !== null && !oneOf(e.only, ATTACK_KINDS)) || (e.per !== undefined && e.per !== null && e.per !== 'count'))
        return null;
    if (e.most !== undefined && (e.per !== 'count' || !isInt(e.most, 1, 10)))
        return null;
    return { key: e.key, label: e.label.trim(), target: e.target, only: e.only ?? null, per: e.per === 'count' ? 'count' : null, ...(typeof e.most === 'number' ? { most: e.most } : {}) };
});
/** The d20 path's situations (§3.2.4). `with` needs dice that take advantage; a tick that does nothing is refused. */
const parseAttackSituations = (v, d) => keyed(v, (e) => {
    if (!Object.keys(e).every((k) => ['key', 'label', 'ac', 'with', 'only'].includes(k)) || (e.ac === undefined && e.with === undefined))
        return null;
    if (e.ac !== undefined && (!isInt(e.ac, -50, 50) || e.ac === 0))
        return null;
    if (e.with !== undefined && (!isAdvantageWord(e.with) || !d.advantage))
        return null;
    if (e.only !== undefined && !oneOf(e.only, ATTACK_KINDS))
        return null;
    return { key: e.key, label: e.label.trim(), ...(typeof e.ac === 'number' ? { ac: e.ac } : {}), ...(isAdvantageWord(e.with) ? { with: e.with } : {}), ...(typeof e.only === 'string' ? { only: e.only } : {}) };
});
const parseViewSituations = (v) => (Array.isArray(v) ? parseAttackSituations(v, { count: 1, sides: 2, advantage: { dice: 1 } }) ?? [] : []);
const isMeleeVis = (x) => x === 'half' || x === 'full';
function parseVisibility(v) {
    if (!isObj(v) || Object.keys(v).some((k) => !['vision', 'conditions', 'melee'].includes(k)) || (v.melee !== undefined && !isMeleeVis(v.melee)))
        return null;
    const vision = keyed(v.vision, (e) => (Object.keys(e).length === 2 ? { key: e.key, label: e.label.trim() } : null));
    if (!vision || !vision.length)
        return null;
    const conditions = keyed(v.conditions, (e) => {
        const t = e.target;
        if (Object.keys(e).some((k) => !['key', 'label', 'target', 'melee'].includes(k)) || (e.melee !== undefined && !isMeleeVis(e.melee)))
            return null;
        if (!isObj(t) || Object.keys(t).length !== vision.length || !vision.every((x) => isInt(t[x.key], 0, STAGED_LIMITS.modifier)))
            return null;
        return { key: e.key, label: e.label.trim(), target: Object.fromEntries(vision.map((x) => [x.key, t[x.key]])), ...(isMeleeVis(e.melee) ? { melee: e.melee } : {}) };
    });
    return conditions && conditions.length ? { vision, conditions, ...(isMeleeVis(v.melee) ? { melee: v.melee } : {}) } : null;
}
/** Melee (issue 1528), strictly: `melee: "opposed"` with its `melee_target` and `melee_ties` (none named: a tie hits nobody). */
function parseMelee(a) {
    if (a.melee === undefined)
        return a.melee_target === undefined && a.melee_ties === undefined ? null : undefined;
    if (a.melee !== 'opposed' || !isInt(a.melee_target, 1, STAGED_LIMITS.target) || (a.melee_ties !== undefined && a.melee_ties !== 'attacker' && a.melee_ties !== 'none'))
        return undefined;
    return { target: a.melee_target, ties: a.melee_ties === 'attacker' ? 'attacker' : 'none' };
}
/**
 * An attack against a target number (issue 1527; §3.2.4), strictly, with the damage block's `codes` and the system's
 * test: null when any part is malformed, so the table offers no staged attack rather than a wrong one.
 */
/** `damage.over_damage` (qa-runs 1169): exact shape or null. */
function parseOverDamage(v) {
    if (!isObj(v) || Object.keys(v).some((k) => !['label', 'times', 'rating'].includes(k)))
        return null;
    if (!isLabel(v.label, 40) || typeof v.times !== 'number' || !(v.times > 0 && v.times <= 10) || !Array.isArray(v.rating) || !v.rating.length || v.rating.length > STAGED_LIMITS.ratingFields)
        return null;
    const rating = [];
    for (const f of v.rating) {
        if (!isObj(f) || Object.keys(f).some((k) => !['purpose', 'which', 'label'].includes(k)) || !isKey(f.purpose) || !isKey(f.which) || (f.label !== undefined && !isLabel(f.label, 40)))
            return null;
        rating.push({ purpose: f.purpose, which: f.which, ...(f.label ? { label: f.label.trim() } : {}) });
    }
    return { label: v.label.trim(), times: v.times, rating };
}
export function parseStaged(a, damage, test) {
    if (!isObj(a) || a.against !== 'target_number' || a.resolve !== 'staged' || !test)
        return null;
    const dice = parseTestDice(a.dice);
    if (!dice || dice.count !== 'rated' || dice.success?.atLeast !== 'target')
        return null;
    const ranges = parseBands(a.ranges);
    const situations = a.situations === undefined ? [] : parseSituations(a.situations);
    const visibility = a.visibility === undefined ? null : parseVisibility(a.visibility);
    if (!ranges || !situations || (a.visibility !== undefined && !visibility))
        return null;
    const codes = isObj(damage) ? parseCodes(damage.codes) : null;
    const overRaw = isObj(damage) ? damage.over_damage : undefined;
    const overDamage = overRaw === undefined ? null : parseOverDamage(overRaw);
    if (overRaw !== undefined && !overDamage)
        return null;
    const t = a.defence_tests;
    const melee = parseMelee(a);
    const firing = a.firing === undefined ? null : parseFiring(a.firing);
    if (!codes || !isObj(t) || melee === undefined || (a.firing !== undefined && !firing))
        return null;
    const poolKey = (x) => (x === undefined ? null : isKey(x) ? x : undefined);
    let dodge = null;
    if (t.dodge !== undefined) {
        const d = t.dodge;
        const pool = isObj(d) ? poolKey(d.pool) : undefined;
        if (!isObj(d) || pool === undefined || !isInt(d.target, 1, STAGED_LIMITS.target) || (d.only !== undefined && !oneOf(d.only, ATTACK_KINDS)))
            return null;
        dodge = { pool, target: d.target, only: d.only ?? null };
    }
    const r = t.resist;
    if (!isObj(r))
        return null;
    const fields = Array.isArray(r.rating) ? r.rating : [r.rating];
    if (!fields.length || fields.length > STAGED_LIMITS.ratingFields || !fields.every((f) => isObj(f) && isKey(f.purpose) && isKey(f.which) && (f.label === undefined || isLabel(f.label, 40))))
        return null;
    const pool = poolKey(r.pool);
    const armour = r.armour === undefined ? {} : r.armour;
    if (pool === undefined || !isObj(armour) || Object.entries(armour).some(([k, w]) => !oneOf(k, ATTACK_KINDS) || !isKey(w)) || (r.wounds !== undefined && typeof r.wounds !== 'boolean'))
        return null;
    return {
        dice, ranges, situations, visibility, dodge,
        resist: { rating: fields.map((f) => ({ purpose: f.purpose, which: f.which, ...(f.label ? { label: f.label.trim() } : {}) })), pool, armour: { ...armour }, wounds: r.wounds === true },
        codes, melee, firing, ...(overDamage ? { overDamage } : {}),
    };
}
const ADDRESS_RE = /^[a-z][a-z0-9_]{0,62}\/[a-z0-9][a-z0-9-]{0,127}$/;
const ratingField = (f) => (isObj(f) && Object.keys(f).every((k) => ['purpose', 'which', 'label'].includes(k)) && isKey(f.purpose) && isKey(f.which) && isLabel(f.label, 40) ? { purpose: f.purpose, which: f.which, label: f.label.trim() } : null);
/** `features.combat.metamagic` (qa-runs#366), strictly: null when any part is malformed, so the table offers none. */
function parseMetamagic(v, kinds) {
    const text = (x, most = 200) => typeof x === 'string' && x.trim().length > 0 && x.length <= most;
    if (!isObj(v) || !isInt(v.highest_level, 1, 99) || !isObj(v.spontaneous) || !Array.isArray(v.feats) || !v.feats.length || v.feats.length > 40)
        return null;
    const sp = v.spontaneous;
    if (!text(sp.from) || !kinds.includes(sp.from) || !text(sp.to) || !kinds.includes(sp.to) || !text(sp.longer))
        return null;
    const feats = [];
    for (const f of v.feats) {
        if (!isObj(f) || !isKey(f.key) || !text(f.feat, 80) || !text(f.label, 40) || !text(f.word, 40))
            return null;
        if (f.slots !== 'choose' && !isInt(f.slots, 0, 9))
            return null;
        if (f.per_level !== undefined && !isInt(f.per_level, 1, 9))
            return null;
        const boost = f.boost === undefined ? null : parseDiceBoost(f.boost);
        if (f.boost !== undefined && !boost)
            return null;
        const a = f.area;
        if (a !== undefined && !(isObj(a) && isInt(a.times, 2, 9) && Array.isArray(a.shapes) && a.shapes.every((x) => text(x, 20)) && text(f.area_refused)))
            return null;
        if (f.casting_time !== undefined && !(text(f.casting_time) && kinds.includes(f.casting_time)))
            return null;
        if (f.no_long !== undefined && !text(f.no_long))
            return null;
        if (f.spontaneous !== undefined && f.spontaneous !== 'refused' && f.spontaneous !== 'exempt')
            return null;
        if (f.spontaneous === 'refused' && !text(f.spontaneous_refused))
            return null;
        const r = f.refused_for;
        if (r !== undefined && !(isObj(r) && Array.isArray(r.casting) && r.casting.every((x) => text(x, 80)) && text(r.reason)))
            return null;
        if (f.note !== undefined && !text(f.note))
            return null;
        if (f.dice_note !== undefined && !text(f.dice_note))
            return null;
        feats.push({
            key: f.key, feat: f.feat, label: f.label, word: f.word, slots: f.slots,
            ...(f.per_level !== undefined ? { perLevel: f.per_level } : {}), ...(boost ? { boost } : {}),
            ...(a !== undefined ? { area: { times: a.times, shapes: [...a.shapes] }, areaRefused: f.area_refused } : {}),
            ...(f.casting_time !== undefined ? { castingTime: f.casting_time } : {}), ...(f.no_long !== undefined ? { noLong: f.no_long } : {}),
            ...(f.spontaneous !== undefined ? { spontaneous: f.spontaneous } : {}), ...(f.spontaneous_refused !== undefined ? { spontaneousRefused: f.spontaneous_refused } : {}),
            ...(r !== undefined ? { refusedFor: { casting: [...r.casting], reason: r.reason } } : {}),
            ...(f.note !== undefined ? { note: f.note } : {}), ...(f.dice_note !== undefined ? { diceNote: f.dice_note } : {}),
        });
    }
    if (new Set(feats.map((f) => f.key)).size !== feats.length)
        return null;
    return { highest: v.highest_level, spontaneous: { from: sp.from, to: sp.to, longer: sp.longer }, feats };
}
/** The rules as the client carries them (qa-runs#366): already in the table's own spelling, read back exactly. */
function parseMetamagicView(v) {
    if (!isObj(v) || !isInt(v.highest, 1, 99) || !isObj(v.spontaneous) || !Array.isArray(v.feats))
        return null;
    const list = v.feats;
    if (!list.every((f) => isObj(f) && isKey(f.key) && typeof f.feat === 'string' && typeof f.label === 'string' && typeof f.word === 'string'))
        return null;
    return v;
}
/**
 * `features.combat.spellcasting` (issue 1530), strictly, with the system's test and damage block: the pool must draw on
 * both `spell` and `drain`, and the drain's kinds be the system's damage kinds. Null when any part is malformed, so the
 * table offers no Cast control rather than a wrong one.
 */
export function parseSpellcasting(v, test, damage, pools) {
    if (!isObj(v) || !test || Object.keys(v).some((k) => !['skill', 'pool', 'magic', 'targets', 'drain', 'magic_dice', 'standing_powers'].includes(k)))
        return null;
    const magicDice = v.magic_dice === undefined ? undefined : parseMagicDice(v.magic_dice);
    if (magicDice === null)
        return null;
    const standingPowers = v.standing_powers === undefined ? undefined : parseStandingPowers(v.standing_powers);
    if (standingPowers === null)
        return null;
    const dice = test.dice;
    if (dice.count !== 'rated' || dice.success?.atLeast !== 'target')
        return null;
    const codes = isObj(damage) ? parseCodes(damage.codes) : null;
    const kinds = isObj(damage) && Array.isArray(damage.kinds) ? damage.kinds.filter(isObj).map((k) => k.key).filter(isKey) : [];
    if (!codes)
        return null;
    if (v.skill !== undefined && (typeof v.skill !== 'string' || !ADDRESS_RE.test(v.skill)))
        return null;
    let pool = null;
    if (v.pool !== undefined) {
        const p = pools?.find((x) => x.key === v.pool);
        if (!p || !(p.for.includes('any') || (p.for.includes('spell') && p.for.includes('drain'))))
            return null;
        pool = p.key;
    }
    const magic = ratingField(v.magic);
    const targets = keyed(v.targets, (e) => {
        if (Object.keys(e).some((k) => !['key', 'label', 'purpose', 'which', 'resists'].includes(k)) || !isKey(e.purpose) || !isKey(e.which) || (e.resists !== undefined && typeof e.resists !== 'boolean'))
            return null;
        return { key: e.key, label: e.label.trim(), purpose: e.purpose, which: e.which, resists: e.resists === true };
    });
    const d = v.drain;
    if (!magic || !targets || !targets.length || targets.length > 12 || !isObj(d) || Object.keys(d).some((k) => !['rating', 'divide', 'beyond_top', 'disaster', 'kinds'].includes(k)))
        return null;
    const rating = ratingField(d.rating);
    const k = d.kinds;
    if (!rating || !isInt(d.divide, 1, 10) || (d.beyond_top !== undefined && !isInt(d.beyond_top, 0, 10)) || (d.disaster !== undefined && !isInt(d.disaster, 0, 10)))
        return null;
    if (!isObj(k) || Object.keys(k).some((x) => x !== 'within' && x !== 'over') || typeof k.within !== 'string' || typeof k.over !== 'string' || !kinds.includes(k.within) || !kinds.includes(k.over))
        return null;
    return {
        dice, skill: typeof v.skill === 'string' ? v.skill : null, pool, magic, targets,
        drain: { rating, divide: d.divide, beyondTop: typeof d.beyond_top === 'number' ? d.beyond_top : 0, disaster: typeof d.disaster === 'number' ? d.disaster : 0, kinds: { within: k.within, over: k.over } },
        codes, ...(magicDice?.length ? { magicDice } : {}), ...(standingPowers ? { standingPowers } : {}),
    };
}
/** The most initiative dice a spirit or an astral state throws (issue 1531): the encounter's own bound. */
const MAX_STATE_DICE = 20;
const isHalf = (v) => typeof v === 'number' && v > 0 && v <= 10 && Number.isInteger(v * 2);
const words = (v, most = 12) => (Array.isArray(v) && v.length >= 1 && v.length <= most && v.every(isKey) && new Set(v).size === v.length ? [...v] : null);
const initiativeOf = (v, fields) => (isObj(v) && Object.keys(v).every((k) => ['from', 'add', 'dice'].includes(k)) && isKey(v.from) && (!fields || fields.includes(v.from)) && isInt(v.add ?? 0, -50, 50) && isInt(v.dice ?? 1, 1, MAX_STATE_DICE)
    ? { from: v.from, add: v.add ?? 0, dice: v.dice ?? 1 } : null);
/**
 * `features.combat.conjuring` (issue 1531), strictly, with the system's test, damage and resources: the drain's levels
 * the damage codes' keys, rising, the last with no bound; its kinds the damage kinds; a spirit's monitors the system's
 * resources and its pools the system's pools. Null when any part is malformed, so the table offers no Conjure control.
 */
export function parseConjuring(v, test, damage, pools, resources) {
    if (!isObj(v) || !test || Object.keys(v).some((k) => !['skill', 'family', 'magic', 'most', 'drain', 'spirit', 'powers'].includes(k)))
        return null;
    const dice = test.dice;
    let powers;
    if (v.powers !== undefined) {
        const pw = v.powers;
        if (!isObj(pw) || Object.keys(pw).some((k) => !['family', 'list', 'element', 'spell', 'force'].includes(k)) || !isKey(pw.family) || !isKey(pw.list) || !isKey(pw.force)
            || (pw.element !== undefined && !isKey(pw.element)) || (pw.spell !== undefined && !isKey(pw.spell)))
            return null;
        powers = { family: pw.family, list: pw.list, element: pw.element ?? null, spell: pw.spell ?? null, force: pw.force };
    }
    if (dice.count !== 'rated' || dice.success?.atLeast !== 'target')
        return null;
    const codes = isObj(damage) ? parseCodes(damage.codes) : null;
    const kinds = isObj(damage) && Array.isArray(damage.kinds) ? damage.kinds.filter(isObj).map((k) => k.key).filter(isKey) : [];
    const resourceKeys = Array.isArray(resources) ? resources.filter(isObj).map((r) => r.key).filter(isKey) : [];
    if (!codes)
        return null;
    if (v.skill !== undefined && (typeof v.skill !== 'string' || !ADDRESS_RE.test(v.skill)))
        return null;
    if (v.family !== undefined && !isKey(v.family))
        return null;
    const magic = ratingField(v.magic);
    if (!magic || (v.most !== undefined && !isInt(v.most, 0, 10)))
        return null;
    const d = v.drain;
    if (!isObj(d) || Object.keys(d).some((k) => !['rating', 'levels', 'kinds'].includes(k)))
        return null;
    const rating = ratingField(d.rating);
    if (!rating || !Array.isArray(d.levels) || d.levels.length < 1 || d.levels.length > codes.levels.length)
        return null;
    const levels = [];
    for (const [i, l] of d.levels.entries()) {
        const last = i === d.levels.length - 1;
        if (!isObj(l) || Object.keys(l).some((k) => k !== 'most' && k !== 'level') || !codes.levels.some((x) => x.key === l.level))
            return null;
        if (last ? l.most !== undefined : !isHalf(l.most))
            return null;
        const prev = levels.at(-1);
        if (prev && (typeof l.most === 'number' && l.most <= prev.most))
            return null;
        if (prev && codes.levels.findIndex((x) => x.key === l.level) <= codes.levels.findIndex((x) => x.key === prev.level))
            return null;
        levels.push({ most: last ? null : l.most, level: l.level });
    }
    const k = d.kinds;
    if (!isObj(k) || Object.keys(k).some((x) => x !== 'within' && x !== 'over') || typeof k.within !== 'string' || typeof k.over !== 'string' || !kinds.includes(k.within) || !kinds.includes(k.over))
        return null;
    const sp = v.spirit;
    if (!isObj(sp) || Object.keys(sp).some((x) => !['variable', 'purpose', 'attributes', 'initiative', 'monitors', 'armour', 'pools', 'state', 'services'].includes(x)))
        return null;
    const attributes = words(sp.attributes, 16);
    if (typeof sp.variable !== 'string' || !/^[A-Za-z]$/.test(sp.variable) || !isKey(sp.purpose) || !attributes)
        return null;
    const initiative = initiativeOf(sp.initiative, attributes);
    if (!initiative || !isObj(sp.monitors))
        return null;
    const monitors = {};
    for (const [key, m] of Object.entries(sp.monitors)) {
        if (!resourceKeys.includes(key) || !(isInt(m, 1, 100) || (typeof m === 'string' && attributes.includes(m))))
            return null;
        monitors[key] = m;
    }
    let armour = null;
    if (sp.armour !== undefined) {
        const a = sp.armour;
        const w = isObj(a) ? words(a.words, 4) : null;
        if (!isObj(a) || Object.keys(a).some((x) => x !== 'times' && x !== 'words') || !isInt(a.times, 1, 10) || !w)
            return null;
        armour = { times: a.times, words: w };
    }
    const poolsOut = [];
    for (const p of Array.isArray(sp.pools) ? sp.pools : sp.pools === undefined ? [] : [null]) {
        if (!isObj(p) || Object.keys(p).some((x) => !['key', 'sum', 'divide', 'services'].includes(x)) || !pools?.some((x) => x.key === p.key) || poolsOut.some((x) => x.key === p.key))
            return null;
        const sum = p.sum === undefined ? undefined : words(p.sum, 8);
        if (sum === null || (sum && sum.some((x) => !attributes.includes(x))) || (p.divide !== undefined && !isInt(p.divide, 1, 10)))
            return null;
        if ((sum === undefined) === (p.services === undefined) || (p.services !== undefined && !isInt(p.services, 0, 10)))
            return null;
        poolsOut.push({ key: p.key, ...(sum ? { sum, divide: p.divide ?? 1 } : { services: p.services }) });
    }
    if ((sp.state !== undefined && !isKey(sp.state)) || (sp.services !== undefined && !isLabel(sp.services, 24)))
        return null;
    return {
        dice, skill: typeof v.skill === 'string' ? v.skill : null, family: typeof v.family === 'string' ? v.family : null, magic, most: typeof v.most === 'number' ? v.most : 0,
        drain: { rating, levels, kinds: { within: k.within, over: k.over } },
        spirit: { variable: sp.variable, purpose: sp.purpose, attributes, initiative, monitors, armour, pools: poolsOut, state: typeof sp.state === 'string' ? sp.state : null, services: typeof sp.services === 'string' ? sp.services.trim() : null },
        codes, ...(powers ? { powers } : {}),
    };
}
/**
 * `features.combat.astral` (issue 1531), strictly, with the system's pools: each state's pool a pool drawn on for
 * `astral`. Null when any part is malformed, so no astral control is offered.
 */
export function parseAstral(v, pools) {
    if (!isObj(v) || Object.keys(v).some((k) => !['purpose', 'states', 'armour'].includes(k)) || !isKey(v.purpose) || (v.armour !== undefined && typeof v.armour !== 'boolean'))
        return null;
    const states = keyed(v.states, (e) => {
        if (Object.keys(e).some((k) => !['key', 'label', 'only', 'attributes', 'initiative', 'pool'].includes(k)) || (e.only !== undefined && typeof e.only !== 'boolean'))
            return null;
        const attributes = {};
        if (e.attributes !== undefined) {
            if (!isObj(e.attributes) || Object.keys(e.attributes).length > 16)
                return null;
            for (const [to, from] of Object.entries(e.attributes)) {
                if (!isKey(to) || !isKey(from))
                    return null;
                attributes[to] = from;
            }
        }
        const initiative = e.initiative === undefined ? null : initiativeOf(e.initiative, null);
        if (e.initiative !== undefined && !initiative)
            return null;
        let pool = null;
        if (e.pool !== undefined) {
            const p = pools?.find((x) => x.key === e.pool);
            if (!p || !(p.for.includes('astral') || p.for.includes('any')))
                return null;
            pool = p.key;
        }
        return { key: e.key, label: e.label.trim(), only: e.only === true, attributes, initiative, pool };
    });
    if (!states || !states.length || states.length > 6)
        return null;
    return { purpose: v.purpose, states, armour: v.armour !== false };
}
/** The most a vehicle block holds (issue 1533). */
export const VEHICLE_RULE_LIMITS = { fields: 16, terrains: 8, tests: 8, rig: 4, add: 10, terrain: 10 };
/**
 * `features.combat.vehicles` (issue 1533), strictly, with the system's test, pools and resources: the pool one drawn
 * on for `vehicle`, the monitors the system's resources, the named fields among `fields`, each test's terrains the
 * system's. Null when any part is malformed, so the table offers no vehicle control.
 */
export function parseVehicles(v, test, pools, resources) {
    const KEYS = ['family', 'purpose', 'fields', 'handling', 'offroad', 'pilot', 'autonav', 'monitors', 'pool', 'rig', 'terrains', 'tests'];
    if (!isObj(v) || !test || Object.keys(v).some((k) => !KEYS.includes(k)))
        return null;
    const dice = test.dice;
    if (dice.count !== 'rated' || dice.success?.atLeast !== 'target')
        return null;
    if ((v.family !== undefined && !isKey(v.family)) || !isKey(v.purpose))
        return null;
    const fields = words(v.fields, VEHICLE_RULE_LIMITS.fields);
    if (!fields)
        return null;
    const named = (x, need) => (x === undefined ? (need ? undefined : null) : typeof x === 'string' && fields.includes(x) ? x : undefined);
    const handling = named(v.handling, true);
    const offroad = named(v.offroad, false);
    const pilot = named(v.pilot, false);
    const autonav = named(v.autonav, false);
    if (!handling || offroad === undefined || pilot === undefined || autonav === undefined)
        return null;
    const resourceKeys = Array.isArray(resources) ? resources.filter(isObj).map((r) => r.key).filter(isKey) : [];
    if (!isObj(v.monitors) || !Object.keys(v.monitors).length)
        return null;
    const monitors = {};
    for (const [key, m] of Object.entries(v.monitors)) {
        if (!resourceKeys.includes(key) || !(isInt(m, 1, 100) || (typeof m === 'string' && fields.includes(m))))
            return null;
        monitors[key] = m;
    }
    let pool = null;
    if (v.pool !== undefined) {
        const p = pools?.find((x) => x.key === v.pool);
        if (!p || !(p.for.includes('vehicle') || p.for.includes('any')))
            return null;
        pool = p.key;
    }
    const rig = ratingField(v.rig);
    const terrains = keyed(v.terrains, (e) => (Object.keys(e).every((k) => k === 'key' || k === 'label') ? { key: e.key, label: e.label.trim() } : null));
    if (!rig || !terrains || terrains.length > VEHICLE_RULE_LIMITS.terrains)
        return null;
    const tests = keyed(v.tests, (e) => {
        if (!Object.keys(e).every((k) => ['key', 'label', 'rig', 'autonav', 'autonav_add', 'terrain'].includes(k)) || !isInt(e.rig ?? 0, 0, VEHICLE_RULE_LIMITS.rig))
            return null;
        if (!(e.autonav === undefined || e.autonav === 'dice' || e.autonav === 'target') || !isInt(e.autonav_add ?? 0, 0, VEHICLE_RULE_LIMITS.add) || (e.autonav_add !== undefined && e.autonav !== 'target'))
            return null;
        const t = e.terrain === undefined ? {} : e.terrain;
        if (!isObj(t) || Object.entries(t).some(([k, x]) => !terrains.some((r) => r.key === k) || !isInt(x, -VEHICLE_RULE_LIMITS.terrain, VEHICLE_RULE_LIMITS.terrain)))
            return null;
        return { key: e.key, label: e.label.trim(), rig: e.rig ?? 0, autonav: e.autonav ?? null, autonavAdd: e.autonav_add ?? 0, terrain: { ...t } };
    });
    if (!tests?.length || tests.length > VEHICLE_RULE_LIMITS.tests)
        return null;
    return { dice, family: typeof v.family === 'string' ? v.family : null, purpose: v.purpose, fields, handling, offroad, pilot, autonav, monitors, pool, rig, terrains, tests };
}
/** The most a creatures block holds (issue 1534). */
export const CREATURE_RULE_LIMITS = { numbers: 24, tests: 12, attacks: 4, label: 40 };
/**
 * `features.combat.creatures` (issue 1534), strictly: a family's key; each number a field, a purpose and its words (at
 * most 8, the field's own when none are given), no field written twice under the same purpose and words; the tests
 * fields among the numbers; each attack a field and a label. Null when any part is malformed, so a linked creature
 * carries only what a stat block always gave it.
 */
export function parseCreatures(v) {
    if (!isObj(v) || Object.keys(v).some((k) => !['family', 'numbers', 'tests', 'attacks'].includes(k)) || !isKey(v.family))
        return null;
    if (!Array.isArray(v.numbers) || !v.numbers.length || v.numbers.length > CREATURE_RULE_LIMITS.numbers)
        return null;
    const numbers = [];
    for (const n of v.numbers) {
        if (!isObj(n) || Object.keys(n).some((k) => !['field', 'purpose', 'words'].includes(k)) || !isKey(n.field) || !isKey(n.purpose))
            return null;
        const named = n.words === undefined ? n.field.split('_') : n.words;
        if (!Array.isArray(named) || !named.length || named.length > 8 || !named.every((w) => typeof w === 'string' && /^[a-z0-9]{1,24}$/.test(w)))
            return null;
        const one = { field: n.field, purpose: n.purpose, words: [...named] };
        if (numbers.some((x) => x.purpose === one.purpose && x.words.join('_') === one.words.join('_')))
            return null;
        numbers.push(one);
    }
    const tests = v.tests === undefined ? [] : words(v.tests, CREATURE_RULE_LIMITS.tests);
    if (!tests || !tests.every((f) => numbers.some((n) => n.field === f)))
        return null;
    const attacks = [];
    if (v.attacks !== undefined) {
        if (!Array.isArray(v.attacks) || v.attacks.length > CREATURE_RULE_LIMITS.attacks)
            return null;
        for (const a of v.attacks) {
            if (!isObj(a) || Object.keys(a).some((k) => k !== 'field' && k !== 'label') || !isKey(a.field) || !isLabel(a.label, CREATURE_RULE_LIMITS.label))
                return null;
            attacks.push({ field: a.field, label: a.label.trim() });
        }
    }
    return { family: v.family, numbers, tests, attacks };
}
/** The most a host's ratings, alerts and tally reach (issue 1532): the request's and the state's own bounds. */
export const MATRIX_LIMITS = { codes: 8, subsystems: 8, alerts: 4, add: 10, least: 5 };
/**
 * `features.combat.matrix` (issue 1532), strictly, with the system's test and pools: the pool one drawn on for
 * `matrix`. Null when any part is malformed, so the table offers no System test.
 */
export function parseMatrix(v, test, pools) {
    if (!isObj(v) || !test || Object.keys(v).some((k) => !['skill', 'pool', 'detection', 'security', 'codes', 'subsystems', 'alerts', 'ties', 'least', 'tally', 'jack_in', 'sheaf', 'cyber'].includes(k)))
        return null;
    const dice = test.dice;
    if (dice.count !== 'rated' || dice.success?.atLeast !== 'target')
        return null;
    if (v.skill !== undefined && (typeof v.skill !== 'string' || !ADDRESS_RE.test(v.skill)))
        return null;
    let pool = null;
    if (v.pool !== undefined) {
        const p = pools?.find((x) => x.key === v.pool);
        if (!p || !(p.for.includes('matrix') || p.for.includes('any')))
            return null;
        pool = p.key;
    }
    const detection = ratingField(v.detection);
    if (!detection || !isLabel(v.security, 40) || !isLabel(v.tally, 40))
        return null;
    const plain = (e) => (Object.keys(e).every((k) => k === 'key' || k === 'label') ? { key: e.key, label: e.label.trim() } : null);
    const codes = keyed(v.codes, plain);
    const subsystems = keyed(v.subsystems, plain);
    const alerts = keyed(v.alerts, (e) => (Object.keys(e).every((k) => ['key', 'label', 'add'].includes(k)) && isInt(e.add, 0, MATRIX_LIMITS.add) ? { key: e.key, label: e.label.trim(), add: e.add } : null));
    if (!codes?.length || codes.length > MATRIX_LIMITS.codes || !subsystems?.length || subsystems.length > MATRIX_LIMITS.subsystems || !alerts || alerts.length > MATRIX_LIMITS.alerts)
        return null;
    if (!(v.ties === 'decker' || v.ties === 'host') || (v.least !== undefined && !isInt(v.least, 0, MATRIX_LIMITS.least)))
        return null;
    let jackIn = null;
    if (v.jack_in !== undefined) {
        const j = v.jack_in;
        if (!isObj(j) || Object.keys(j).some((k) => !['label', 'reaction', 'dice'].includes(k)) || !isLabel(j.label, 40))
            return null;
        const reaction = ratingField(j.reaction);
        const jd = ratingField(j.dice);
        if (!reaction || !jd)
            return null;
        jackIn = { label: j.label.trim(), reaction, dice: jd };
    }
    let sheaf = null;
    if (v.sheaf !== undefined) {
        const h = v.sheaf;
        if (!isObj(h) || Object.keys(h).some((k) => !['family', 'ic_dice', 'every_test'].includes(k)) || !isKey(h.family) || !isObj(h.ic_dice))
            return null;
        const icDice = {};
        for (const c of codes) {
            const n = h.ic_dice[c.key];
            if (!isInt(n, 1, 10))
                return null;
            icDice[c.key] = n;
        }
        if (Object.keys(h.ic_dice).length !== codes.length)
            return null;
        let everyTest = null;
        if (h.every_test !== undefined) {
            const e = h.every_test;
            if (!isObj(e) || Object.keys(e).some((k) => k !== 'field' && k !== 'is') || !isKey(e.field) || !isLabel(e.is, 40))
                return null;
            everyTest = { field: e.field, is: e.is };
        }
        sheaf = { family: h.family, icDice, everyTest };
    }
    let cyber = null;
    if (v.cyber !== undefined) {
        cyber = parseCyber(v.cyber, codes.map((c) => c.key));
        if (!cyber)
            return null;
    }
    return {
        dice, skill: typeof v.skill === 'string' ? v.skill : null, pool, detection, security: v.security.trim(), codes, subsystems, alerts,
        ties: v.ties, least: typeof v.least === 'number' ? v.least : 0, tally: v.tally.trim(),
        ...(jackIn ? { jackIn } : {}), ...(sheaf ? { sheaf } : {}), ...(cyber ? { cyber } : {}),
    };
}
export function parsePools(v) {
    if (!Array.isArray(v) || !v.length || v.length > MAX_POOLS)
        return null;
    const out = [];
    for (const e of v) {
        if (!isObj(e) || Object.keys(e).some((k) => !['key', 'label', 'sheet', 'refresh', 'for', 'cap', 'uses'].includes(k)))
            return null;
        if (!isKey(e.key) || !isLabel(e.label) || out.some((p) => p.key === e.key) || !oneOf(e.refresh, POOL_REFRESH))
            return null;
        if (e.sheet !== undefined && e.sheet !== null && !isKey(e.sheet))
            return null;
        if (!Array.isArray(e.for) || !e.for.length || !e.for.every((w) => oneOf(w, POOL_FOR)) || new Set(e.for).size !== e.for.length)
            return null;
        const kinds = e.for;
        const cap = {};
        if (e.cap !== undefined) {
            if (!isObj(e.cap))
                return null;
            for (const [k, w] of Object.entries(e.cap)) {
                if (!oneOf(k, POOL_FOR) || !(kinds.includes(k) || kinds.includes('any')) || !oneOf(w, POOL_CAP))
                    return null;
                cap[k] = w;
            }
        }
        const uses = e.uses === undefined ? [] : e.uses;
        if (!Array.isArray(uses) || !uses.every((w) => oneOf(w, POOL_USES)) || new Set(uses).size !== uses.length)
            return null;
        out.push({ key: e.key, label: e.label.trim(), sheet: typeof e.sheet === 'string' ? e.sheet : null, refresh: e.refresh, for: [...kinds], cap, uses: [...uses] });
    }
    return out;
}
/** Whether a throw is a plain sum of its dice: no explosion, no kept dice, no success count. */
export const isPlainDice = (d) => !d.explode && !d.keep && !d.success;
/**
 * The lowest and highest results a throw of these dice can make: a total of the kept dice, each with its explosions; a
 * success count from none to every kept die and its every extra roll.
 */
export const diceRange = (d) => {
    const kept = d.keep ? d.keep.n : d.count;
    const rolls = 1 + (d.explode ? d.explode.limit : 0);
    return d.success ? { min: 0, max: kept * (d.success.chains ? 1 : rolls) } : { min: kept, max: kept * d.sides * rolls };
};
/** "d20", "2d6"; "4d6 keep highest 3", "5d10 exploding on 10, successes on 8+" (issue 1312). */
export const diceText = (d) => {
    const parts = [`${d.count === 1 ? '' : d.count}d${d.sides}${d.keep ? ` keep ${d.keep.which} ${d.keep.n}` : ''}`];
    if (d.explode)
        parts.push(`exploding on ${d.explode.on}${d.explode.on < d.sides ? '+' : ''}${d.explode.until !== undefined ? ` to reach ${d.explode.until}` : ''}`);
    if (d.success)
        parts.push(`successes on ${d.success.atLeast}+${d.success.chains ? ' by each die\'s sum' : ''}`);
    return parts.join(', ');
};
function attack(v) {
    if (!isObj(v))
        return null;
    const d = dice(v.dice);
    if (!d || !oneOf(v.against, ['defence', 'number']))
        return null;
    const { min, max } = diceRange(d);
    const number = v.against === 'number' ? (isInt(v.number, -1000, 100_000) ? v.number : null) : null;
    if (v.against === 'number' && number === null)
        return null;
    // A success count has no single natural face and no threat range (issue 1312): its strong results are its tiers.
    if (d.success && (v.natural !== undefined || v.critical !== undefined))
        return null;
    const natural = [];
    for (const n of Array.isArray(v.natural) ? v.natural : v.natural === undefined ? [] : [null]) {
        if (!isObj(n) || !isInt(n.face, min, max) || !oneOf(n.result, ['hit', 'miss']))
            return null;
        natural.push({ face: n.face, result: n.result });
    }
    const tiers = v.tiers === undefined ? [] : parseTiers(v.tiers);
    if (!tiers)
        return null;
    let critical = null;
    if (v.critical !== undefined) {
        const c = v.critical;
        if (!isObj(c) || !isInt(c.threat, min, max) || !isInt(c.multiplier, 2, 10) || !oneOf(c.confirm, ['roll_again', 'none']) || (c.multiplies !== undefined && c.multiplies !== 'dice'))
            return null;
        critical = { threat: c.threat, multiplier: c.multiplier, confirm: c.confirm, ...(c.multiplies === 'dice' ? { multiplies: 'dice' } : {}) };
    }
    const situations = v.situations === undefined ? null : parseAttackSituations(v.situations, d);
    if (v.situations !== undefined && !situations)
        return null;
    if (v.full_action !== undefined && !isKey(v.full_action))
        return null;
    return { dice: d, against: v.against, number, natural, critical, marginToDamage: v.margin_to_damage === true, tiers, ...(situations?.length ? { situations } : {}), ...(typeof v.full_action === 'string' ? { fullAction: v.full_action } : {}) };
}
/**
 * Outcome tiers (issue 1312), strictly: one to 24, rising by `min` (a whole-number margin), keys once each, a tier at or
 * above 0 never keyed `miss` and one below never `hit`. Null otherwise.
 */
export function parseTiers(v) {
    if (!Array.isArray(v) || v.length < 1 || v.length > 24)
        return null;
    const out = [];
    for (const t of v) {
        if (!isObj(t) || Object.keys(t).some((k) => !['min', 'key', 'label'].includes(k)))
            return null;
        if (!isInt(t.min, -MAX_MARGIN, MAX_MARGIN) || !isKey(t.key) || !isLabel(t.label))
            return null;
        if ((t.min >= 0 && t.key === 'miss') || (t.min < 0 && t.key === 'hit'))
            return null;
        if (out.length && t.min <= out[out.length - 1].min)
            return null;
        out.push({ min: t.min, key: t.key, label: t.label.trim() });
    }
    return new Set(out.map((t) => t.key)).size === out.length ? out : null;
}
function keyed(v, each) {
    if (v === undefined)
        return [];
    if (!Array.isArray(v) || v.length > 24)
        return null;
    const out = [];
    for (const e of v) {
        if (!isObj(e) || !isKey(e.key) || !isLabel(e.label))
            return null;
        const got = each(e);
        if (got === null)
            return null;
        out.push(got);
    }
    return new Set(v.map((e) => e.key)).size === v.length ? out : null;
}
function initiative(v) {
    if (!isObj(v))
        return null;
    // A rated count (issue 1525) throws plain dice, each combatant its own number of them: read as one die of the sides.
    const rated = isObj(v.dice) && v.dice.count === 'rated';
    const d = rated ? dice({ ...v.dice, count: 1 }) : dice(v.dice);
    if (!d || (rated && (!isPlainDice(d) || d.advantage)) || !oneOf(v.order, ['highest', 'lowest']))
        return null;
    const ties = v.ties === undefined ? [] : v.ties;
    if (!Array.isArray(ties) || !ties.every((t) => oneOf(t, ['first_pass', 'modifier', 'roll_off'])) || new Set(ties).size !== ties.length)
        return null;
    let passes;
    if (v.passes !== undefined) {
        const p = v.passes;
        if (!isObj(p) || Object.keys(p).some((k) => k !== 'step') || !isInt(p.step, 1, 100) || v.order !== 'highest')
            return null;
        passes = { step: p.step };
    }
    if (v.reroll !== undefined && v.reroll !== 'each_round')
        return null;
    return { dice: d, order: v.order, ties: ties, ...(rated ? { rated: true } : {}), ...(passes ? { passes } : {}), ...(v.reroll === 'each_round' ? { reroll: 'each_round' } : {}) };
}
function distance(v) {
    if (!isObj(v) || !isLabel(v.unit, 12) || typeof v.per_square !== 'number' || !(v.per_square > 0) || v.per_square > 1000)
        return null;
    if (!oneOf(v.diagonal, ['alternate', 'one', 'two', 'straight']))
        return null;
    // Difficult terrain (issue 1151): absent is none (nothing defaulted); malformed leaves the whole rule unread.
    let difficult = null;
    if (v.difficult !== undefined) {
        const d = v.difficult;
        if (!isObj(d) || Object.keys(d).some((k) => k !== 'multiplier'))
            return null;
        if (typeof d.multiplier !== 'number' || !Number.isFinite(d.multiplier) || !(d.multiplier > 1) || d.multiplier > 10)
            return null;
        difficult = { multiplier: d.multiplier };
    }
    // How a token moves (qa-runs#1161): absent is none (no area, no kinds); malformed leaves the whole rule unread.
    let mv;
    if (v.movement !== undefined) {
        const m = movement(v.movement);
        if (!m)
            return null;
        mv = m;
    }
    // The ground a creature takes: absent, every token stands on one square; malformed leaves the whole rule unread.
    let space;
    if (v.space !== undefined) {
        const s = v.space;
        if (!isObj(s) || Object.keys(s).some((k) => !['field', 'size', 'sizes'].includes(k)) || !isKey(s.field) || !isKey(s.size) || !isObj(s.sizes))
            return null;
        const sizes = Object.entries(s.sizes);
        if (sizes.length > 20 || !sizes.every(([k, n]) => isKey(k) && typeof n === 'number' && n > 0 && n <= 1000))
            return null;
        space = { field: s.field, size: s.size, sizes: Object.fromEntries(sizes) };
    }
    return { unit: v.unit.trim(), perSquare: v.per_square, diagonal: v.diagonal, difficult, ...(mv ? { movement: mv } : {}), ...(space ? { space } : {}) };
}
/** The most kinds of move a system names, and the most actions one spends. */
export const MOVEMENT_LIMITS = { kinds: 8, spends: 4, times: 10 };
/** `distance.movement` as the data writes it (snake case) or as a view carries it back (`heavyTimes`). */
function movement(v) {
    if (!isObj(v) || Object.keys(v).some((k) => !['speed', 'heavy', 'kinds', 'fly', 'unseen', 'owed', 'split'].includes(k)) || (v.split !== undefined && v.split !== true))
        return null;
    const purposeOf = (x) => (isObj(x) && Object.keys(x).length === 1 && isKey(x.purpose) ? { purpose: x.purpose } : null);
    const textOf = (x) => {
        if ((x.field !== undefined && !isKey(x.field)) || (x.pattern !== undefined && (typeof x.pattern !== 'string' || x.pattern.length > 80)))
            return null;
        if (typeof x.pattern === 'string') {
            try {
                new RegExp(x.pattern, 'i');
            }
            catch {
                return null;
            }
        }
        return { ...(typeof x.field === 'string' ? { field: x.field } : {}), ...(typeof x.pattern === 'string' ? { pattern: x.pattern } : {}) };
    };
    if (!isObj(v.speed) || Object.keys(v.speed).some((x) => !['purpose', 'field', 'pattern'].includes(x)) || !isKey(v.speed.purpose))
        return null;
    const speedText = textOf(v.speed);
    if (!speedText)
        return null;
    const speed = { purpose: v.speed.purpose, ...speedText };
    const heavy = v.heavy === undefined || v.heavy === null ? null : purposeOf(v.heavy);
    if (v.heavy !== undefined && v.heavy !== null && !heavy)
        return null;
    if (!Array.isArray(v.kinds) || v.kinds.length < 1 || v.kinds.length > MOVEMENT_LIMITS.kinds)
        return null;
    const times = (x) => typeof x === 'number' && Number.isFinite(x) && x > 0 && x <= MOVEMENT_LIMITS.times;
    const kinds = [];
    for (const k of v.kinds) {
        if (!isObj(k) || Object.keys(k).some((x) => !['key', 'label', 'times', 'heavy_times', 'heavyTimes', 'spends', 'difficult', 'squares', 'alone', 'fight_only', 'fightOnly', 'provokes', 'charge', 'adds'].includes(x)) || (k.adds !== undefined && (k.adds !== true || v.split !== true)))
            return null;
        const fo = k.fight_only ?? k.fightOnly;
        const charge = k.charge === undefined ? null : parseCharge(k.charge);
        if (k.charge !== undefined && !charge)
            return null;
        if (k.squares !== undefined && !times(k.squares))
            return null;
        if ((k.alone !== undefined && k.alone !== true) || (fo !== undefined && fo !== true))
            return null;
        if (k.provokes !== undefined && k.provokes !== 'none' && k.provokes !== 'after_first')
            return null;
        const ht = k.heavy_times ?? k.heavyTimes;
        if (!isKey(k.key) || !isLabel(k.label, 40) || !times(k.times) || (ht !== undefined && !times(ht)))
            return null;
        if (!Array.isArray(k.spends) || k.spends.length > MOVEMENT_LIMITS.spends || !k.spends.every(isKey))
            return null;
        if (k.difficult !== undefined && k.difficult !== false)
            return null;
        if (kinds.some((x) => x.key === k.key))
            return null;
        kinds.push({ key: k.key, label: k.label.trim(), times: k.times, ...(ht !== undefined ? { heavyTimes: ht } : {}), spends: [...k.spends], ...(k.difficult === false ? { difficult: false } : {}),
            ...(k.squares !== undefined ? { squares: k.squares } : {}), ...(k.alone === true ? { alone: true } : {}),
            ...(fo === true ? { fightOnly: true } : {}), ...(k.provokes !== undefined ? { provokes: k.provokes } : {}), ...(k.adds === true ? { adds: true } : {}), ...(charge ? { charge } : {}) });
    }
    let fly;
    if (v.fly !== undefined) {
        const f = v.fly;
        if (!isObj(f) || Object.keys(f).some((x) => !['purpose', 'condition', 'field', 'pattern'].includes(x)) || !isKey(f.purpose) || !isLabel(f.condition, 40))
            return null;
        const flyText = textOf(f);
        if (!flyText)
            return null;
        fly = { purpose: f.purpose, condition: f.condition.trim(), ...flyText };
    }
    let unseen;
    if (v.unseen !== undefined) {
        if (!Array.isArray(v.unseen) || v.unseen.length > 8 || !v.unseen.every((x) => isLabel(x, 40)))
            return null;
        unseen = v.unseen.map((x) => x.trim());
    }
    let owed;
    if (v.owed !== undefined) {
        const o = v.owed;
        if (!isObj(o) || Object.keys(o).some((x) => !['condition', 'less', 'no_alone', 'noAlone'].includes(x)) || !isLabel(o.condition, 40) || typeof o.less !== 'number' || !(o.less >= 0) || o.less > 1000)
            return null;
        const na = o.no_alone ?? o.noAlone;
        if (na !== undefined && na !== true)
            return null;
        owed = { condition: o.condition.trim(), less: o.less, ...(na === true ? { noAlone: true } : {}) };
    }
    return { speed, heavy, kinds, ...(v.split === true ? { split: true } : {}), ...(fly ? { fly } : {}), ...(unseen ? { unseen } : {}), ...(owed ? { owed } : {}) };
}
/** A copy of the movement rule, nothing shared. */
export const copyMovement = (m) => ({ speed: { ...m.speed }, heavy: m.heavy ? { ...m.heavy } : null, kinds: m.kinds.map((k) => ({ ...k, spends: [...k.spends] })), ...(m.split ? { split: true } : {}), ...(m.fly ? { fly: { ...m.fly } } : {}), ...(m.unseen ? { unseen: [...m.unseen] } : {}), ...(m.owed ? { owed: { ...m.owed } } : {}) });
function areas(v) {
    if (!isObj(v) || !oneOf(v.origin, ['intersection', 'square']))
        return null;
    if (v.includes !== undefined && !oneOf(v.includes, AREA_INCLUDES))
        return null;
    return { origin: v.origin, includes: v.includes === undefined ? null : v.includes };
}
/** `features.combat.saves`, strictly: the dice, at least one save, each natural face within the dice. Null otherwise. */
function saves(v) {
    if (!isObj(v))
        return null;
    const d = dice(v.dice);
    if (!d || !Array.isArray(v.list) || v.list.length < 1)
        return null;
    const list = keyed(v.list, (e) => ({ key: e.key, label: e.label.trim(), purpose: isKey(e.purpose) ? e.purpose : null }));
    if (!list)
        return null;
    const { min, max } = diceRange(d);
    const natural = [];
    if (d.success && v.natural !== undefined)
        return null;
    if (v.natural !== undefined && !Array.isArray(v.natural))
        return null;
    for (const n of (v.natural ?? [])) {
        if (!isObj(n) || !isInt(n.face, min, max) || !oneOf(n.result, ['success', 'failure']))
            return null;
        natural.push({ face: n.face, result: n.result });
    }
    if (new Set(natural.map((n) => n.face)).size !== natural.length)
        return null;
    return { dice: d, list, natural };
}
/**
 * `features.combat.actions`, strictly (issue 1131): at least one kind, each with its per-turn count (a whole number from
 * 0 to 20, or null for any number); each trade naming kinds of the list. Null otherwise; nothing defaulted.
 */
export function parseActions(v) {
    if (!isObj(v) || !Array.isArray(v.kinds) || v.kinds.length < 1)
        return null;
    const kinds = keyed(v.kinds, (e) => (e.per_turn === null || isInt(e.per_turn, 0, 20)
        && (e.per === undefined || e.per === 'round') && (e.turn_flag === undefined || e.turn_flag === 'no_provoke')
        ? { key: e.key, label: e.label.trim(), perTurn: e.per_turn, ...(e.per === 'round' ? { per: 'round' } : {}), ...(e.turn_flag === 'no_provoke' ? { turnFlag: 'no_provoke' } : {}) } : null));
    if (!kinds)
        return null;
    const known = new Set(kinds.map((k) => k.key));
    if (v.trades !== undefined && (!Array.isArray(v.trades) || v.trades.length > 24))
        return null;
    const trades = [];
    for (const t of (v.trades ?? [])) {
        if (!isObj(t) || !isKey(t.get) || !known.has(t.get) || !Array.isArray(t.give) || t.give.length < 1 || t.give.length > 24)
            return null;
        if (!t.give.every((g) => isKey(g) && known.has(g)))
            return null;
        trades.push({ get: t.get, give: [...t.give] });
    }
    return { kinds, trades };
}
const OUTCOMES = ['hit', 'miss', 'critical', 'threat', 'success', 'failure', 'disaster'];
const LABELLED = ['health', 'initiative', 'challenge'];
/** The validator's bound on a number's word (`MAX_SHORT_LABEL`, scripts/mrcs/combat-rules.js): it sits on the board. */
const MAX_NUMBER_LABEL = 24;
const isNumberLabel = (v) => isLabel(v, MAX_NUMBER_LABEL);
/** `opportunity`: its `until_acted` with the names that make an exception, each a label. */
/** A character's dying (docs/MRCS/dnd-5e.md §3.3), strictly; anything else is none. */
function parseCharacterDeath(v) {
    if (!isObj(v) || !isObj(v.saves) || !isInt(v.floor, -1000, 1000))
        return null;
    const s = v.saves;
    if (!isObj(s.dice) || s.dice.count !== 1 || !isInt(s.dice.sides, 2, 1000) || !isInt(s.target, 1, 1000) || !isInt(s.successes, 1, 20) || !isInt(s.failures, 1, 20) || !isInt(s.damage_fails, 0, 20) || !isInt(s.critical_fails, 0, 20))
        return null;
    const natural = Array.isArray(s.natural) ? s.natural.filter((n) => isObj(n) && isInt(n.face, 1, 1000) && (n.result === 'regain_1' || n.result === 'two_failures')).map((n) => ({ face: n.face, result: n.result })) : [];
    const name = (x) => (isLabel(x, 40) ? x.trim() : null);
    return { floor: v.floor, massive: isKey(v.massive) ? v.massive : null, onZero: name(v.on_zero), stable: name(v.stable), saves: { sides: s.dice.sides, target: s.target, successes: s.successes, failures: s.failures, natural, damageFails: s.damage_fails, criticalFails: s.critical_fails } };
}
function opportunity(v) {
    const u = v.until_acted;
    const moving = Array.isArray(v.moving) ? v.moving.filter((m) => isObj(m) && isLabel(m.feat) && isInt(m.ac, 1, 50)).map((m) => ({ feat: m.feat.trim(), ac: m.ac })).slice(0, 8) : [];
    const count = isObj(v.per_round) && isInt(v.per_round.count, 1, 50) ? v.per_round.count : null;
    const adds = isObj(v.per_round) && Array.isArray(v.per_round.adds) ? v.per_round.adds.filter((a) => isObj(a) && isLabel(a.feat) && isLabel(a.purpose) && isLabel(a.which)).map((a) => ({ feat: a.feat.trim(), purpose: a.purpose, which: a.which })).slice(0, 8) : [];
    const casting = parseCasting(v.casting);
    const also = { ...(v.leaves === 'reach' ? { leaves: 'reach' } : {}), ...(moving.length ? { moving } : {}), ...(count !== null ? { perRound: { count, adds } } : {}), ...(casting ? { casting } : {}) };
    if (!isObj(u))
        return { untilActed: null, ...also };
    const unless = Array.isArray(u.unless) ? u.unless.filter((n) => isLabel(n)).map((n) => n.trim()).slice(0, 24) : [];
    return { untilActed: { unless, condition: isLabel(u.condition) ? u.condition.trim() : null }, ...also };
}
/** `features.combat` as the site serves it: the parts the table reads, each null (or empty) when absent or malformed. */
export function parseCombatRules(v) {
    if (!isObj(v))
        return null;
    const outcomes = {};
    if (isObj(v.outcomes))
        for (const k of OUTCOMES)
            if (isLabel(v.outcomes[k]))
                outcomes[k] = v.outcomes[k].trim();
    const labels = {};
    if (isObj(v.labels))
        for (const k of LABELLED)
            if (isNumberLabel(v.labels[k]))
                labels[k] = v.labels[k].trim();
    const resources = keyed(v.resources, (e) => {
        if (!isNumberLabel(e.label) || typeof e.max !== 'boolean' || !(e.purpose === undefined || e.purpose === 'health'))
            return null;
        // Wound levels (issue 1526) count boxes filled: only on a resource with a maximum, and malformed is no resource.
        const levels = e.levels === undefined ? undefined : e.max ? parseLevels(e.levels) : null;
        if (levels === null)
            return null;
        return { key: e.key, label: e.label.trim(), max: e.max, health: e.purpose === 'health', stat: isKey(e.stat) ? e.stat : null, sheet: isKey(e.sheet) ? e.sheet : null, ...(levels ? { levels } : {}) };
    });
    const okResources = resources && resources.length <= MAX_RESOURCES && resources.filter((r) => r.health).length <= 1 ? resources : [];
    // Damage names resources the system has, each once; anything else is no order at all, and damage lowers hit points.
    const chainOf = (to) => (Array.isArray(to) && to.every((k) => okResources.some((r) => r.key === k)) && new Set(to).size === to.length ? [...to] : null);
    const toRaw = isObj(v.damage) ? v.damage.to : undefined;
    // The kinds of damage (issue 1526), and a chain per kind: every kind its own well-formed chain, or no order at all.
    const kinds = isObj(v.damage) && v.damage.kinds !== undefined ? keyed(v.damage.kinds, (e) => (isNumberLabel(e.label) ? { key: e.key, label: e.label.trim() } : null)) : null;
    const damageKinds = kinds && kinds.length && kinds.length <= 8 ? kinds : null;
    let damageChains = null;
    if (isObj(toRaw) && damageKinds && Object.keys(toRaw).every((k) => damageKinds.some((x) => x.key === k)) && damageKinds.every((k) => chainOf(toRaw[k.key])?.length)) {
        damageChains = Object.fromEntries(damageKinds.map((k) => [k.key, chainOf(toRaw[k.key])]));
    }
    const damageTo = damageChains ? [...damageChains[damageKinds[0].key]] : chainOf(Array.isArray(toRaw) ? toRaw : []) ?? [];
    const minimum = isObj(v.damage) && isObj(v.damage.minimum) && isInt(v.damage.minimum.value, 0, 1000) ? v.damage.minimum.value : null;
    const damageRules = parseDamageRules(v.damage);
    const conditionReads = Array.isArray(v.condition_reads) ? v.condition_reads.filter((k) => CONDITION_READS.includes(k)) : [];
    const test = v.test === undefined ? null : parseTest(v.test);
    const pools = v.pools === undefined ? null : parsePools(v.pools);
    const staged = isObj(v.attack) && v.attack.against === 'target_number' ? parseStaged(v.attack, v.damage, test) : null;
    const spellcasting = v.spellcasting === undefined ? null : parseSpellcasting(v.spellcasting, test, v.damage, pools);
    const metamagic = v.metamagic === undefined ? null : parseMetamagic(v.metamagic, isObj(v.actions) && Array.isArray(v.actions.kinds) ? v.actions.kinds.filter(isObj).map((k) => String(k.key)) : []);
    const conjuring = v.conjuring === undefined ? null : parseConjuring(v.conjuring, test, v.damage, pools, v.resources);
    const astral = v.astral === undefined ? null : parseAstral(v.astral, pools);
    const matrix = v.matrix === undefined ? null : parseMatrix(v.matrix, test, pools);
    const vehicles = v.vehicles === undefined ? null : parseVehicles(v.vehicles, test, pools, v.resources);
    const creatures = v.creatures === undefined ? null : parseCreatures(v.creatures);
    const typeKinds = v.creature_types === undefined ? null : creatureTypes(v.creature_types);
    const casting = parseCastRules(v.casting);
    const concentration = parseHoldRule(v.concentration);
    const deathCharacter = isObj(v.death) ? parseCharacterDeath(v.death.character) : null;
    const death = isObj(v.death) && isInt(v.death.at, -1000, 1000) && (v.death.less === undefined || v.death.less === 'con') ? { at: v.death.at, less: v.death.less === 'con' ? 'con' : null, ...(deathCharacter ? { character: deathCharacter } : {}) } : null;
    const minimumKind = minimum !== null && isKey(v.damage.minimum.kind) ? v.damage.minimum.kind : null;
    return {
        attack: v.attack === undefined ? null : attack(v.attack),
        actions: v.actions === undefined ? null : parseActions(v.actions),
        saves: v.saves === undefined ? null : saves(v.saves),
        defences: keyed(v.defences, (e) => ({ key: e.key, label: e.label.trim(), purpose: isKey(e.purpose) ? e.purpose : null, untilActed: e.until_acted === true, stat: isKey(e.stat) ? e.stat : null })) ?? [],
        concealment: keyed(v.concealment, (e) => (isInt(e.miss_chance, 1, 100) ? { key: e.key, label: e.label.trim(), missChance: e.miss_chance } : null)) ?? [],
        minimumDamage: minimum,
        ...(minimumKind ? { minimumDamageKind: minimumKind } : {}),
        ...(damageRules ? { damageRules } : {}),
        ...(conditionReads.length ? { conditionReads } : {}),
        initiative: v.initiative === undefined ? null : initiative(v.initiative),
        distance: v.distance === undefined ? null : distance(v.distance),
        areas: v.areas === undefined ? null : areas(v.areas),
        outcomes,
        labels,
        resources: okResources,
        damageTo,
        ...(damageKinds ? { damageKinds } : {}),
        ...(damageChains ? { damageChains } : {}),
        vision: v.vision === undefined ? [] : parseVisionRules(v.vision),
        ...(isObj(v.opportunity) ? { opportunity: opportunity(v.opportunity) } : {}),
        ...(isObj(v.maneuvers) && parseManeuverRules(v.maneuvers) ? { maneuvers: parseManeuverRules(v.maneuvers) } : {}),
        ...(v.move_feats !== undefined && parseMoveFeats(v.move_feats) ? { moveFeats: parseMoveFeats(v.move_feats) } : {}),
        ...(v.specials !== undefined && parseSpecialRules(v.specials) ? { specials: parseSpecialRules(v.specials) } : {}),
        ...(v.ready !== undefined && parseReadyRule(v.ready) ? { ready: parseReadyRule(v.ready) } : {}),
        ...(v.reach !== undefined && parseWeaponReach(v.reach) ? { reach: parseWeaponReach(v.reach) } : {}),
        ...(v.miss_chance !== undefined && parseMissChance(v.miss_chance) ? { missChance: parseMissChance(v.miss_chance) } : {}),
        ...(parseCover(v.cover) ? { cover: parseCover(v.cover) } : {}),
        ...(parseStances(v.stances).length ? { stances: parseStances(v.stances) } : {}),
        ...(parseFlanking(v.flanking) ? { flanking: parseFlanking(v.flanking) } : {}),
        ...(parseSneakAttack(v.sneak_attack) ? { sneakAttack: parseSneakAttack(v.sneak_attack) } : {}),
        ...(test ? { test } : {}),
        ...(pools ? { pools } : {}),
        ...(staged ? { staged } : {}),
        ...(spellcasting ? { spellcasting } : {}),
        ...(metamagic ? { metamagic } : {}),
        ...(conjuring ? { conjuring } : {}),
        ...(astral ? { astral } : {}),
        ...(matrix ? { matrix } : {}),
        ...(vehicles ? { vehicles } : {}),
        ...(isObj(v.vehicle_combat) ? { vehicleCombatData: v.vehicle_combat } : {}),
        ...(creatures ? { creatures } : {}),
        ...(typeKinds ? { creatureTypes: typeKinds } : {}),
        ...(casting ? { casting } : {}),
        ...(concentration ? { concentration } : {}),
        ...(death ? { death } : {}),
        weather: v.weather === undefined ? [] : parseWeatherRules(v.weather, Array.isArray(v.concealment) ? v.concealment.filter(isObj).map((c) => c.key).filter(isKey) : []),
    };
}
/** `features.effects.stacking`: which bonus types add up. Null when absent or malformed. */
export function parseStacking(v) {
    if (!isObj(v) || !oneOf(v.default, ['highest', 'stack']) || !isObj(v.types))
        return null;
    const types = {};
    for (const [k, r] of Object.entries(v.types)) {
        if (!isKey(k) || !oneOf(r, ['highest', 'stack']))
            return null;
        types[k] = r;
    }
    return { default: v.default, types };
}
/** `features.effects.units`: the units of time an effect lasts in, smallest first. Null when absent, empty or malformed. */
export function parseUnits(v) {
    if (!Array.isArray(v) || v.length < 1 || v.length > 24)
        return null;
    const out = [];
    for (const u of v) {
        // The label and plural may be left out, as the site's validator allows (`scripts/mrcs/effect-rules.js`): read as the
        // site reads them, the key and the key with an s. Given, they must be what the validator accepts.
        if (!isObj(u) || !isKey(u.key) || !isInt(u.seconds, 1, 1e9))
            return null;
        if ((u.label !== undefined && !isLabel(u.label, 40)) || (u.plural !== undefined && !isLabel(u.plural, 40)))
            return null;
        const label = u.label === undefined ? u.key : u.label.trim();
        const plural = u.plural === undefined ? `${u.key}s` : u.plural.trim();
        out.push({ key: u.key, label, plural, seconds: u.seconds });
    }
    if (new Set(out.map((u) => u.key)).size !== out.length)
        return null;
    return out.sort((a, b) => a.seconds - b.seconds);
}
/**
 * `features.rest` (issue 1482): its kinds by key and label, nothing else — the site's `POST /api/characters/:id/rest`
 * reads the restores itself. Empty when absent or malformed.
 */
export function parseRests(v) {
    if (!isObj(v) || !Array.isArray(v.kinds))
        return [];
    return keyed(v.kinds, (e) => ({ key: e.key, label: e.label.trim(), ...(typeof e.note === 'string' && e.note.trim() ? { note: e.note.trim() } : {}) })) ?? [];
}
/**
 * The registry's `rules` — `{ combat, effects: { stacking, units } }`, each null when the system lacks it — read whole:
 * every part checked, nothing assumed. The snapshot carries the same object to the browser, which reads it here too.
 */
export function parseSystemRules(v) {
    if (!isObj(v))
        return noRules();
    const fx = isObj(v.effects) ? v.effects : {};
    const sources = parseSources(v.sources);
    const rests = parseRests(v.rest);
    return { combat: parseCombatRules(v.combat), stacking: parseStacking(fx.stacking), units: parseUnits(fx.units), ...(rests.length ? { rests } : {}), ...(Object.keys(sources).length ? { sources } : {}) };
}
// ── What the table can do with them ──────────────────────────────────────────
/**
 * Why the table cannot make an attack in this system, or null when it can: an attack roll against one of the
 * system's defences. A system that rolls against a fixed number or adds the margin to damage is not automated yet.
 */
export const NO_ATTACK_RULES = "This table's game system has no attack rules, so the table cannot resolve an attack.";
export function attackRefusal(rules) {
    const a = rules?.combat?.attack;
    if (!a)
        return NO_ATTACK_RULES;
    // An attack against the system's fixed number (issue 1312: Traveller's 8+) needs no defence on the target.
    if (a.against === 'number')
        return a.number === null ? NO_ATTACK_RULES : null;
    if (!rules.combat.defences.length)
        return "The table cannot resolve this system's attacks yet: they are not rolled against a defence.";
    // The token's own number is the one defence the table always has (`values.ac`): a system none of whose defences sits
    // there has nothing to roll against (a bug check of issue 1303).
    if (!mainDefence(rules.combat))
        return "The table cannot resolve this system's attacks: none of its defences is held on a token's armour class, so there is nothing to roll against.";
    return null;
}
/**
 * The resource the board's health bar shows (issue 1312): the one whose purpose is `health` and that damage lowers
 * first. Null when the system has none, and a token's health is its hit points.
 */
export function healthResource(c) {
    const first = c?.damageTo[0];
    const r = first ? c.resources.find((x) => x.key === first) : undefined;
    return r?.health ? r : null;
}
/**
 * The chain a kind of damage goes down (issue 1526; `damage.to` keyed by kind): no kind named, the first kind's (or the
 * one list); a kind of the system's, its own chain, or the one list when the system writes one for every kind. Null: a
 * kind the system does not name.
 */
export function damageChain(c, kind) {
    if (kind === undefined)
        return c.damageTo;
    if (!c.damageKinds?.some((k) => k.key === kind))
        return null;
    return c.damageChains?.[kind] ?? c.damageTo;
}
/** The last resource of each chain damage goes down (issue 1526): full, the next damage kills (§5.4). */
export function chainEnds(c) {
    const chains = c.damageChains ? Object.values(c.damageChains) : [c.damageTo];
    return [...new Set(chains.map((ch) => ch[ch.length - 1]).filter((k) => typeof k === 'string'))];
}
/** The system's resources as the wounds read them (issue 1526): the health, the levels, the end of a chain. */
export function woundResources(c) {
    if (!c || !c.resources.some((r) => r.levels?.length))
        return [];
    const ends = chainEnds(c);
    const health = healthResource({ resources: c.resources, damageTo: c.damageTo });
    return c.resources.map((r) => ({ key: r.key, label: r.label, ...(r === health ? { health: true } : {}), ...(r.levels ? { levels: r.levels } : {}), ...(ends.includes(r.key) ? { end: true } : {}) }));
}
/** A token's wounds by the system's rules (issue 1526): none on a system whose resources have no levels. */
export const combatWounds = (c, resources) => woundsOf(woundResources(c), resources);
/**
 * The wounds' changes to the combatants' scores (issue 1526; §5.3, §5.4): for each combatant, its first token on the
 * scene's wound initiative modifier, where it differs from the one it carries (0: none). Empty when nothing changes, or
 * the system's resources have no levels.
 */
export function woundChanges(state, c) {
    const rules = woundResources(c);
    if (!rules.length)
        return [];
    const out = [];
    for (const k of state.encounter.combatants) {
        const token = k.tokens.map((id) => state.objects[id]).find((o) => o !== undefined);
        const now = token ? Math.max(-100, Math.min(100, woundsOf(rules, token.values.resources).initiative)) : 0;
        if (now !== (k.woundModifier ?? 0))
            out.push({ id: k.id, modifier: now });
    }
    return out;
}
/**
 * A linked token's resources (issue 1137, decision A): each resource that names where it is read from (`stat` on a
 * creature's stat block, `sheet` on a character's sheet) and whose source holds a number there, over what the token
 * already carries. A creature sets current and maximum alike, as a stat block's hit points do. A sheet's number is the
 * maximum of a resource that has one, and the token keeps its current (the damage taken at the table) or, new, starts
 * full; of a resource without one, it is the current. A maximum is above 0; a fraction is cut. null when nothing was
 * read, so a token of a system whose resources name no source is left as it is.
 */
export function linkedResources(resources, from, read, current) {
    const out = { ...(current ?? {}) };
    let any = false;
    for (const r of resources) {
        const at = r[from];
        const raw = at ? read(at) : null;
        if (raw === null || !Number.isFinite(raw))
            continue;
        const n = Math.trunc(raw);
        if (!r.max) {
            out[r.key] = { current: n };
            any = true;
            continue;
        }
        if (n <= 0)
            continue;
        const had = current?.[r.key];
        out[r.key] = { current: from === 'sheet' && had ? had.current : n, max: n };
        any = true;
    }
    return any ? out : null;
}
/** What an attack against a fixed number is stamped as being against (issue 1312): no defence of the target's. */
export const NUMBER_DEFENCE = 'number';
export const NUMBER_LABEL = 'target number';
/** The defence held on a token's armour-class register (`values.ac`): the one whose sheet purpose is `armor_class`. */
export const mainDefence = (c) => c.defences.find((d) => d.purpose === 'armor_class') ?? null;
/**
 * A monster's defences other than its main one, by the system's defence keys, read from its stat block (a bug check of
 * issue 1303): from the field the defence names (`stat`: 3.5's `touch_ac`, Pathfinder's `cmd`), else the field of its
 * own key. A defence the block lacks is null: the DM is asked for it at the attack.
 */
export function monsterDefences(c, field) {
    const out = {};
    if (!c)
        return out;
    const main = mainDefence(c);
    for (const d of c.defences) {
        if (d === main)
            continue;
        out[d.key] = field(d.stat ?? d.key);
    }
    return out;
}
/**
 * Whether a system has combat rules to play or lend (issue 1305): any part but its words. A block naming only its
 * result or number words (issue 1309) rules nothing.
 */
export function hasCombatRules(c) {
    return Boolean(c && (c.attack || c.actions || c.saves || c.defences.length || c.concealment.length || c.minimumDamage !== null || c.initiative || c.distance || c.areas));
}
/** A system's words for a token's numbers (issue 1309): its `labels`, and its armour-class defence's label; null where none. */
export function numberLabels(c) {
    const main = c ? mainDefence(c) : null;
    return {
        health: c?.labels.health ?? null,
        defence: main && isNumberLabel(main.label) ? main.label : null,
        initiative: c?.labels.initiative ?? null,
        challenge: c?.labels.challenge ?? null,
    };
}
/** The defence a creature has until it acts in a fight (flat-footed), if the system has one. */
export const untilActedDefence = (c) => c.defences.find((d) => d.untilActed) ?? null;
/** The unit a round of a fight is: the system's shortest unit of time. The encounter ticks once per round. */
export const roundUnit = (rules) => rules?.units?.[0] ?? null;
export const vehicleView = (r) => ({
    family: r.family, pool: r.pool, rig: r.rig.label, offroad: r.offroad !== null, pilot: r.pilot !== null, minTarget: r.dice.success?.minTarget ?? 1,
    terrains: r.terrains.map((x) => ({ ...x })), tests: r.tests.map((t) => ({ key: t.key, label: t.label, autonav: t.autonav })),
});
function parseVehicleView(v) {
    if (!isObj(v) || !(v.family === null || isKey(v.family)) || !(v.pool === null || isKey(v.pool)) || !isLabel(v.rig, 40) || typeof v.offroad !== 'boolean' || typeof v.pilot !== 'boolean' || !isInt(v.minTarget, 1, MAX_SIDES))
        return null;
    const terrains = keyed(v.terrains, () => ({}));
    const tests = keyed(v.tests, (e) => (e.autonav === null || e.autonav === 'dice' || e.autonav === 'target' ? {} : null));
    if (!terrains || !tests?.length)
        return null;
    return {
        family: v.family, pool: v.pool, rig: v.rig, offroad: v.offroad, pilot: v.pilot, minTarget: v.minTarget,
        terrains: v.terrains.map((t) => ({ key: t.key, label: t.label })),
        tests: v.tests.map((t) => ({ key: t.key, label: t.label, autonav: t.autonav })),
    };
}
export const matrixView = (m) => ({
    skill: m.skill, pool: m.pool, detection: m.detection.label, security: m.security, tally: m.tally, minTarget: m.dice.success?.minTarget ?? 1,
    codes: m.codes.map((x) => ({ ...x })), subsystems: m.subsystems.map((x) => ({ ...x })), alerts: m.alerts.map((x) => ({ ...x })),
    ...(m.jackIn ? { jackIn: m.jackIn.label } : {}), ...(m.sheaf ? { sheaf: m.sheaf.family } : {}), ...(m.cyber ? { cyber: true } : {}),
});
function parseMatrixView(v) {
    if (!isObj(v) || !(v.skill === null || (typeof v.skill === 'string' && ADDRESS_RE.test(v.skill))) || !(v.pool === null || isKey(v.pool)) || !isLabel(v.detection, 40) || !isLabel(v.security, 40) || !isLabel(v.tally, 40) || !isInt(v.minTarget, 1, MAX_SIDES))
        return null;
    const plain = () => ({});
    const codes = keyed(v.codes, plain);
    const subsystems = keyed(v.subsystems, plain);
    const alerts = keyed(v.alerts, (e) => (isInt(e.add, 0, MATRIX_LIMITS.add) ? {} : null));
    if (!codes?.length || !subsystems?.length || !alerts)
        return null;
    const kl = (x) => ({ key: x.key, label: x.label });
    return {
        skill: v.skill, pool: v.pool, detection: v.detection, security: v.security, tally: v.tally, minTarget: v.minTarget,
        codes: v.codes.map(kl), subsystems: v.subsystems.map(kl), alerts: v.alerts.map((a) => ({ key: a.key, label: a.label, add: a.add })),
        ...(isLabel(v.jackIn, 40) ? { jackIn: v.jackIn } : {}), ...(isKey(v.sheaf) ? { sheaf: v.sheaf } : {}), ...(v.cyber === true ? { cyber: true } : {}),
    };
}
export const conjuringView = (c) => ({ skill: c.skill, family: c.family, magic: c.magic.label, drain: c.drain.rating.label, most: c.most, minTarget: c.dice.success?.minTarget ?? 1 });
function parseConjuringView(v) {
    if (!isObj(v) || !(v.skill === null || (typeof v.skill === 'string' && ADDRESS_RE.test(v.skill))) || !(v.family === null || isKey(v.family)) || !isLabel(v.magic, 40) || !isLabel(v.drain, 40) || !isInt(v.most, 0, 10) || !isInt(v.minTarget, 1, MAX_SIDES))
        return null;
    return { skill: v.skill, family: v.family, magic: v.magic, drain: v.drain, most: v.most, minTarget: v.minTarget };
}
export const astralView = (a) => ({ states: a.states.map((x) => ({ key: x.key, label: x.label, only: x.only, pool: x.pool })) });
function parseAstralView(v) {
    const states = isObj(v) ? keyed(v.states, (e) => (typeof e.only === 'boolean' && (e.pool === null || isKey(e.pool)) ? { key: e.key, label: e.label, only: e.only, pool: e.pool } : null)) : null;
    return states && states.length ? { states } : null;
}
export const spellcastingView = (s) => ({
    skill: s.skill, pool: s.pool, magic: s.magic.label, targets: s.targets.map((t) => ({ key: t.key, label: t.label, resists: t.resists })),
    codes: parseCodes(codesData(s.codes)), minTarget: s.dice.success?.minTarget ?? 1, divide: s.drain.divide,
    ...(s.magicDice ? { magicDice: parseMagicDice(JSON.parse(JSON.stringify(s.magicDice))) } : {}),
    ...(s.standingPowers ? { standingPowers: { ...s.standingPowers } } : {}),
});
function parseSpellcastingView(v) {
    if (!isObj(v) || !(v.skill === null || (typeof v.skill === 'string' && ADDRESS_RE.test(v.skill))) || !(v.pool === null || isKey(v.pool)) || !isLabel(v.magic, 40) || !isInt(v.minTarget, 1, MAX_SIDES) || !isInt(v.divide, 1, 10))
        return null;
    const targets = keyed(v.targets, (e) => (typeof e.resists === 'boolean' ? { key: e.key, label: e.label, resists: e.resists } : null));
    const codes = isObj(v.codes) ? parseCodes({ levels: v.codes.levels, stage_per: v.codes.stagePer, beyond_top: v.codes.beyondTop, ...(v.codes.wielder !== undefined ? { wielder: v.codes.wielder } : {}) }) : null;
    if (!targets || !codes)
        return null;
    const magicDice = v.magicDice === undefined ? undefined : parseMagicDice(v.magicDice);
    if (magicDice === null)
        return null;
    const standingPowers = v.standingPowers === undefined ? undefined : parseStandingPowers(v.standingPowers);
    if (standingPowers === null)
        return null;
    return { skill: v.skill, pool: v.pool, magic: v.magic, targets, codes, minTarget: v.minTarget, divide: v.divide, ...(magicDice ? { magicDice } : {}), ...(standingPowers ? { standingPowers } : {}) };
}
function parseCastRules(v) {
    if (!isObj(v))
        return null;
    const out = { ...(v.slots === 'by_level' ? { slots: 'by_level' } : {}), ...(v.upcast === true ? { upcast: true } : {}), ...(v.rituals === true ? { rituals: true } : {}) };
    return Object.keys(out).length ? out : null;
}
function parseHoldRule(v) {
    if (!isObj(v) || typeof v.save !== 'string' || !isObj(v.target) || !isInt(v.target.least, 1, 1000))
        return null;
    if (v.target.most !== undefined && !isInt(v.target.most, 1, 1000))
        return null;
    return { save: v.save, least: v.target.least, half: v.target.half_damage === true, most: v.target.most ?? null, endsOn: Array.isArray(v.ends_on) ? v.ends_on.filter((w) => w === 'dead') : [] };
}
/** The save's DC for this much damage: half of it where the system says, at least `least`, at most `most`. Pure. */
export function holdDc(r, amount) {
    const dc = Math.max(r.least, r.half ? Math.floor(amount / 2) : 0);
    return r.most === null ? dc : Math.min(dc, r.most);
}
export function conditionsCount(c, on) {
    if (!c)
        return false;
    const reads = c.conditionReads ?? [];
    if (on === 'initiative')
        return !!c.initiative?.dice.advantage || reads.includes('initiative');
    if (on === 'save')
        return !!c.saves?.dice.advantage || reads.includes('save');
    return !!c.saves?.dice.advantage || reads.includes('ability_check') || reads.includes('skill');
}
const copyVisibility = (v) => ({ vision: v.vision.map((x) => ({ ...x })), conditions: v.conditions.map((c) => ({ ...c, target: { ...c.target } })), ...(v.melee ? { melee: v.melee } : {}) });
export const stagedView = (s) => ({
    ranges: s.ranges.map((b) => ({ ...b })), situations: s.situations.map((x) => ({ ...x })), visibility: s.visibility ? copyVisibility(s.visibility) : null,
    dodge: s.dodge ? { pool: s.dodge.pool, target: s.dodge.target } : null, resistPool: s.resist.pool, codes: parseCodes(codesData(s.codes)), minTarget: s.dice.success?.minTarget ?? 1,
    ...(s.melee ? { melee: { ...s.melee } } : {}),
    ...(s.firing ? { firing: firingData(s.firing) } : {}),
});
function parseStagedView(v) {
    if (!isObj(v))
        return null;
    const ranges = parseBands(v.ranges);
    const situations = parseSituations(v.situations);
    const visibility = v.visibility === null ? null : parseVisibility(v.visibility);
    const codes = isObj(v.codes) ? parseCodes({ levels: v.codes.levels, stage_per: v.codes.stagePer, beyond_top: v.codes.beyondTop, ...(v.codes.wielder !== undefined ? { wielder: v.codes.wielder } : {}) }) : null;
    const d = v.dodge;
    const dodge = d === null ? null : isObj(d) && (d.pool === null || isKey(d.pool)) && isInt(d.target, 1, STAGED_LIMITS.target) ? { pool: d.pool, target: d.target } : undefined;
    if (!ranges || !situations || (v.visibility !== null && !visibility) || !codes || dodge === undefined || !(v.resistPool === null || isKey(v.resistPool)) || !isInt(v.minTarget, 1, MAX_SIDES))
        return null;
    const m = v.melee;
    const melee = m === undefined ? null : isObj(m) && isInt(m.target, 1, STAGED_LIMITS.target) && (m.ties === 'attacker' || m.ties === 'none') ? { target: m.target, ties: m.ties } : undefined;
    if (melee === undefined)
        return null;
    const firing = v.firing === undefined ? null : parseFiring(v.firing);
    if (v.firing !== undefined && !firing)
        return null;
    return { ranges, situations, visibility, dodge, resistPool: v.resistPool, codes, minTarget: v.minTarget, ...(melee ? { melee } : {}), ...(firing ? { firing: firingData(firing) } : {}) };
}
const testView = (t) => ({ open: Boolean(t.open), minTarget: t.dice.success?.minTarget ?? 1, defaulting: t.defaulting ? { limit: t.defaulting.limit, routes: t.defaulting.routes.map((r) => ({ ...r })) } : null });
function parseTestView(v) {
    if (!isObj(v) || typeof v.open !== 'boolean' || !isInt(v.minTarget, 1, MAX_SIDES))
        return null;
    let defaulting = null;
    if (v.defaulting !== null) {
        const g = v.defaulting;
        if (!isObj(g) || !(g.limit === null || isInt(g.limit, 2, MAX_SIDES)) || !Array.isArray(g.routes) || g.routes.length > 24)
            return null;
        const routes = [];
        for (const r of g.routes) {
            if (!isObj(r) || !oneOf(r.to, DEFAULT_TO) || !isInt(r.target, 0, 20) || !oneOf(r.pool, DEFAULT_POOL))
                return null;
            routes.push({ to: r.to, target: r.target, pool: r.pool });
        }
        defaulting = { limit: g.limit, routes };
    }
    return { open: v.open, minTarget: v.minTarget, defaulting };
}
export function rulesView(r) {
    const c = r?.combat ?? null;
    const refusal = attackRefusal(r);
    const a = c?.attack;
    const main = c ? mainDefence(c) : null;
    const health = healthResource(c);
    // Against a fixed number (issue 1312) the attack is made against the one "defence" the table stamps for it.
    if (!refusal && a && c && a.against === 'number') {
        return { ...rulesView({ ...r, combat: { ...c, attack: { ...a, against: 'defence' }, defences: [{ key: NUMBER_DEFENCE, label: NUMBER_LABEL, purpose: 'armor_class', untilActed: false, stat: null }] } }), labels: r?.labels ? { ...r.labels } : numberLabels(c) };
    }
    return {
        attack: !refusal && a && c && main ? {
            main: main.key, touch: c.defences.find((d) => d.key === 'touch')?.key ?? main.key,
            dice: advantageDice(a.dice, null), critical: a.critical ? { threat: a.critical.threat, multiplier: a.critical.multiplier } : null,
            ...(a.dice.advantage ? { advantage: true } : {}), ...(a.situations ? { situations: a.situations.map((x) => ({ ...x })) } : {}),
            defences: c.defences.map((d) => ({ key: d.key, label: d.label })), concealment: c.concealment.map((x) => ({ label: x.label, missChance: x.missChance })),
            ...(c.cover?.improved ? { improvedCover: c.cover.improved.label } : {}),
            ...(c.stances?.some((s) => s.with === 'attack') ? { stances: c.stances.filter((s) => s.with === 'attack').map(stanceView) } : {}),
        } : null,
        ...(c?.stances?.some((s) => s.with === 'action') ? { actionStances: c.stances.filter((s) => s.with === 'action').map(stanceView) } : {}),
        ...(c?.opportunity?.casting?.defensive ? { castDefensive: c.opportunity.casting.defensive.label } : {}),
        ...(c?.cover?.improved?.reflex ? { improvedSave: { label: c.cover.improved.label, save: c.cover.save } } : {}),
        attackRefusal: refusal,
        distance: c?.distance ? { ...c.distance, difficult: c.distance.difficult ? { ...c.distance.difficult } : null, ...(c.distance.movement ? { movement: copyMovement(c.distance.movement) } : {}) } : null,
        areaIncludes: c?.areas?.includes ?? null,
        units: (r?.units ?? []).map((u) => ({ ...u })),
        bonusTypes: r?.stacking ? Object.keys(r.stacking.types) : [],
        initiative: Boolean(c?.initiative),
        ...(c?.initiative && (c.initiative.rated || c.initiative.passes || c.initiative.reroll) ? { passes: { rated: c.initiative.rated === true, sides: c.initiative.dice.sides, step: c.initiative.passes?.step ?? null, reroll: c.initiative.reroll === 'each_round' } } : {}),
        saves: (c?.saves?.list ?? []).map((x) => ({ key: x.key, label: x.label })),
        ...(c?.saves?.dice.advantage ? { saveAdvantage: true } : {}),
        ...(c?.casting ? { casting: { ...c.casting } } : {}),
        ...(c?.concentration ? { concentrates: true } : {}),
        ...(c?.conditionReads ? { conditionReads: [...c.conditionReads] } : {}),
        ...(c?.death?.character ? { deathMarks: { successes: c.death.character.saves.successes, failures: c.death.character.saves.failures } } : {}),
        ...(c?.initiative?.dice.advantage ? { initiativeAdvantage: true } : {}),
        borrowed: r?.borrowed ?? null,
        borrowUnavailable: r?.borrowUnavailable ?? null,
        labels: r?.labels ? { ...r.labels } : numberLabels(c),
        actions: c?.actions ? copyActions(c.actions) : null,
        ...(c?.ready ? { ready: { ...c.ready, kinds: [...c.ready.kinds] } } : {}),
        ...(c?.damageRules ? { damage: { temporary: c.damageRules.temporary, stops: stopsDamage(c.damageRules), types: c.damageRules.types.map((t) => ({ ...t })) } } : {}),
        ...(c?.reach ? { reach: { sizes: c.reach.sizes.map((x) => ({ ...x })), weapons: c.reach.weapons.map((w) => ({ ...w, names: [...w.names] })) } } : {}),
        resources: (() => { const ends = c && c.resources.some((x) => x.levels?.length) ? chainEnds(c) : []; return (c?.resources ?? []).map((x) => ({ key: x.key, label: x.label, max: x.max, ...(x.levels ? { levels: copyLevels(x.levels) } : {}), ...(ends.includes(x.key) ? { end: true } : {}) })); })(),
        ...(c?.damageKinds ? { damageKinds: c.damageKinds.map((k) => ({ ...k })) } : {}),
        health: health?.key ?? null,
        vision: (c?.vision ?? []).map((x) => ({ ...x })),
        weather: (c?.weather ?? []).map((w) => ({ key: w.key, label: w.label, notes: w.notes })),
        rests: (r?.rests ?? []).map((k) => ({ key: k.key, label: k.label, ...(k.note ? { note: k.note } : {}) })),
        ...(c?.test ? { test: testView(c.test) } : {}),
        ...(c?.pools ? { pools: c.pools.map(({ sheet: _sheet, ...p }) => ({ ...p, for: [...p.for], cap: { ...p.cap }, uses: [...p.uses] })) } : {}),
        ...(c?.staged ? { staged: stagedView(c.staged) } : {}),
        ...(c?.spellcasting ? { spellcasting: spellcastingView(c.spellcasting) } : {}),
        ...(c?.metamagic ? { metamagic: c.metamagic } : {}),
        ...(c?.conjuring ? { conjuring: conjuringView(c.conjuring) } : {}),
        ...(c?.astral ? { astral: astralView(c.astral) } : {}),
        ...(c?.matrix ? { matrix: matrixView(c.matrix) } : {}),
        ...(c?.vehicles ? { vehicles: vehicleView(c.vehicles) } : {}),
        ...(c?.vehicleCombatData ? { vehicleCombatData: c.vehicleCombatData } : {}),
        ...(c?.maneuvers ? { maneuvers: maneuverView(c.maneuvers) } : {}),
    };
}
/** A view as the snapshot carries it: read back through the data's own parsers, so a malformed part is none. */
export function parseRulesView(v) {
    if (!isObj(v))
        return rulesView(null);
    const a = isObj(v.attack) ? v.attack : null;
    const d = isObj(v.distance) ? distance({ unit: v.distance.unit, per_square: v.distance.perSquare, diagonal: v.distance.diagonal, ...(isObj(v.distance.difficult) ? { difficult: v.distance.difficult } : {}), ...(isObj(v.distance.movement) ? { movement: v.distance.movement } : {}) }) : null;
    const viewDice = a ? parseTableDice(a.dice) : null;
    const attackPart = a && viewDice ? attack({ dice: diceData(viewDice), against: 'defence', ...(isObj(a.critical) ? { critical: { ...a.critical, confirm: 'none' } } : {}) }) : null;
    const defences = a ? keyed(a.defences, () => ({})) : null;
    const concealment = a && Array.isArray(a.concealment) && a.concealment.every((x) => isObj(x) && isLabel(x.label) && isInt(x.missChance, 1, 100)) ? a.concealment : null;
    return {
        attack: attackPart && defences && concealment && isKey(a.main) && isKey(a.touch) ? {
            main: a.main, touch: a.touch,
            dice: attackPart.dice, critical: attackPart.critical ? { threat: attackPart.critical.threat, multiplier: attackPart.critical.multiplier } : null,
            defences: a.defences.map((x) => ({ key: x.key, label: x.label })), concealment: concealment.map((x) => ({ label: x.label, missChance: x.missChance })),
            ...(isLabel(a.improvedCover) ? { improvedCover: a.improvedCover } : {}),
            ...(a.advantage === true ? { advantage: true } : {}),
            ...(() => { const s = parseViewSituations(a.situations); return s.length ? { situations: s } : {}; })(),
            ...(stanceViews(a.stances).length ? { stances: stanceViews(a.stances) } : {}),
        } : null,
        ...(stanceViews(v.actionStances).length ? { actionStances: stanceViews(v.actionStances) } : {}),
        ...(isLabel(v.castDefensive) ? { castDefensive: v.castDefensive } : {}),
        ...(isObj(v.improvedSave) && isLabel(v.improvedSave.label) && isKey(v.improvedSave.save) ? { improvedSave: { label: v.improvedSave.label, save: v.improvedSave.save } } : {}),
        attackRefusal: typeof v.attackRefusal === 'string' ? v.attackRefusal : null,
        distance: d,
        areaIncludes: oneOf(v.areaIncludes, AREA_INCLUDES) ? v.areaIncludes : null,
        units: parseUnits(v.units) ?? [],
        bonusTypes: Array.isArray(v.bonusTypes) ? v.bonusTypes.filter(isKey) : [],
        initiative: v.initiative === true,
        ...(v.saveAdvantage === true ? { saveAdvantage: true } : {}),
        ...(isObj(v.deathMarks) && isInt(v.deathMarks.successes, 1, 20) && isInt(v.deathMarks.failures, 1, 20) ? { deathMarks: { successes: v.deathMarks.successes, failures: v.deathMarks.failures } } : {}),
        ...(() => { const cast = parseCastRules(v.casting); return cast ? { casting: cast } : {}; })(),
        ...(v.concentrates === true ? { concentrates: true } : {}),
        ...(Array.isArray(v.conditionReads) ? { conditionReads: v.conditionReads.filter((k) => typeof k === 'string').slice(0, 12) } : {}),
        ...(v.initiativeAdvantage === true ? { initiativeAdvantage: true } : {}),
        ...(() => {
            const r = isObj(v.reach) && Array.isArray(v.reach.weapons) ? parseWeaponReach({ stat: 'reach', size_stat: 'size', sizes: v.reach.sizes, weapons: v.reach.weapons.map((w) => { if (!isObj(w))
                    return w; const { noThreat, ...rest } = w; return { ...rest, ...(noThreat ? { no_threat: true } : {}) }; }) }) : null;
            return r ? { reach: { sizes: r.sizes, weapons: r.weapons } } : {};
        })(),
        ...(isObj(v.passes) && typeof v.passes.rated === 'boolean' && typeof v.passes.reroll === 'boolean' && isInt(v.passes.sides, 2, MAX_SIDES) && (v.passes.step === null || isInt(v.passes.step, 1, 100))
            ? { passes: { rated: v.passes.rated, sides: v.passes.sides, step: v.passes.step, reroll: v.passes.reroll } } : {}),
        saves: keyed(v.saves, (e) => ({ key: e.key, label: e.label })) ?? [],
        borrowed: typeof v.borrowed === 'string' && v.borrowed.trim() && v.borrowed.length <= 120 ? v.borrowed : null,
        borrowUnavailable: typeof v.borrowUnavailable === 'string' && v.borrowUnavailable.trim() && v.borrowUnavailable.length <= 120 ? v.borrowUnavailable : null,
        labels: parseNumberLabels(v.labels),
        actions: isObj(v.actions) ? parseActions({
            kinds: Array.isArray(v.actions.kinds) ? v.actions.kinds.map((k) => (isObj(k) ? { key: k.key, label: k.label, per_turn: k.perTurn, ...(k.per !== undefined ? { per: k.per } : {}), ...(k.turnFlag !== undefined ? { turn_flag: k.turnFlag } : {}) } : k)) : v.actions.kinds,
            trades: v.actions.trades,
        }) : null,
        resources: (keyed(v.resources, (e) => {
            if (!isNumberLabel(e.label) || typeof e.max !== 'boolean')
                return null;
            const levels = e.levels === undefined ? undefined : parseLevels(e.levels);
            if (levels === null)
                return null;
            return { key: e.key, label: e.label, max: e.max, ...(levels ? { levels } : {}), ...(e.end === true ? { end: true } : {}) };
        }) ?? []).slice(0, MAX_RESOURCES),
        ...(() => { const k = v.damageKinds === undefined ? null : keyed(v.damageKinds, (e) => (isNumberLabel(e.label) ? { key: e.key, label: e.label } : null)); return k && k.length ? { damageKinds: k } : {}; })(),
        health: isKey(v.health) && Array.isArray(v.resources) && v.resources.some((x) => isObj(x) && x.key === v.health) ? v.health : null,
        vision: parseVisionRules(v.vision),
        weather: (keyed(v.weather, (e) => (isLabel(e.label) && (e.notes === null || (typeof e.notes === 'string' && e.notes.length <= WEATHER_LIMITS.notes))
            ? { key: e.key, label: e.label, notes: e.notes } : null)) ?? []),
        rests: keyed(v.rests, (e) => ({ key: e.key, label: e.label, ...(typeof e.note === 'string' ? { note: e.note } : {}) })) ?? [],
        ...(() => { const t = v.test === undefined ? null : parseTestView(v.test); return t ? { test: t } : {}; })(),
        ...(() => { const p = v.pools === undefined ? null : parsePools(v.pools); return p ? { pools: p.map(({ sheet: _sheet, ...x }) => x) } : {}; })(),
        ...(() => { const st = v.staged === undefined ? null : parseStagedView(v.staged); return st ? { staged: st } : {}; })(),
        ...(() => { const sp = v.spellcasting === undefined ? null : parseSpellcastingView(v.spellcasting); return sp ? { spellcasting: sp } : {}; })(),
        ...(() => { const m = v.metamagic === undefined ? null : parseMetamagicView(v.metamagic); return m ? { metamagic: m } : {}; })(),
        ...(() => { const r = v.ready === undefined ? null : parseReadyRule(v.ready); return r ? { ready: r } : {}; })(),
        ...(() => { const cj = v.conjuring === undefined ? null : parseConjuringView(v.conjuring); return cj ? { conjuring: cj } : {}; })(),
        ...(() => { const mv = v.maneuvers === undefined ? null : parseManeuverView(v.maneuvers); return mv ? { maneuvers: mv } : {}; })(),
        ...(isObj(v.damage) && typeof v.damage.temporary === 'boolean' && typeof v.damage.stops === 'boolean' ? { damage: { temporary: v.damage.temporary, stops: v.damage.stops, types: Array.isArray(v.damage.types) ? v.damage.types.filter((t) => isObj(t) && typeof t.key === 'string' && typeof t.label === 'string' && typeof t.physical === 'boolean') : [] } } : {}),
        ...(() => { const as = v.astral === undefined ? null : parseAstralView(v.astral); return as ? { astral: as } : {}; })(),
        ...(() => { const mx = v.matrix === undefined ? null : parseMatrixView(v.matrix); return mx ? { matrix: mx } : {}; })(),
        ...(() => { const vh = v.vehicles === undefined ? null : parseVehicleView(v.vehicles); return vh ? { vehicles: vh } : {}; })(),
        ...(isObj(v.vehicleCombatData) ? { vehicleCombatData: v.vehicleCombatData } : {}),
    };
}
const copyActions = (a) => ({ kinds: a.kinds.map((k) => ({ ...k })), trades: a.trades.map((t) => ({ get: t.get, give: [...t.give] })) });
/** Number words as a snapshot or the server carries them: each a word within the bound, else null. */
export function parseNumberLabels(v) {
    const o = isObj(v) ? v : {};
    const w = (x) => (isNumberLabel(x) ? x.trim() : null);
    return { health: w(o.health), defence: w(o.defence), initiative: w(o.initiative), challenge: w(o.challenge) };
}
/** What an attack read from a stat block is made with where it says nothing, by the view (see `attackDefaults`). */
export function viewDefaults(v) {
    return v.attack ? { main: v.attack.main, touch: v.attack.touch, critical: v.attack.critical } : null;
}
//# sourceMappingURL=system.js.map